import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db } from './firebase';
import { doc, getDoc, collection, getDocs, addDoc, serverTimestamp, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import BlogComments from './BlogComments';
import RelicsView, { PASSIVE_RELICS, EXTRA_RELICS, getRelicImageSrc } from './RelicsView';
import ClassesView from './ClassesView';
import ItemsView, { rarityStyle } from './ItemsView';
import RunesView from './RunesView';
import ChaosTowerPage from './ChaosTowerPage';
import MercenariosPage from './MercenariosPage';
import MineracaoPage from './MineracaoPage';
import ChavesPage from './ChavesPage';
import AugmentsPage, { AUGMENTS_DATA } from './AugmentsPage';
import GemasJoiasPage from './GemasJoiasPage';
import CharmsPage, { CHARM_DB } from './CharmsPage';

import Equipe from './Equipe';

const TWITCH_CONTAINER_ID = 'twitch-embed-spacezone';

const ALL_RELICS = [...PASSIVE_RELICS, ...EXTRA_RELICS];

const relicImageFor = (rel) => {
    return getRelicImageSrc(rel?.name);
  };

  const handleRelicError = (e) => {
    e.target.style.display = 'none';
  };

const CLASS_DATA = [
  { name: 'Prophet', type: 'MAGIC', destacado: true },
  { name: 'Viking', type: 'MELEE' },
  { name: 'Pyromancer', type: 'MAGIC' },
  { name: 'Marksman', type: 'RANGED' },
  { name: 'Pirate', type: 'RANGED' },
  { name: 'Nomad', type: 'MELEE' },
  { name: 'Redneck', type: 'MELEE' },
  { name: 'Necromancer', type: 'MAGIC' },
  { name: 'Samurai', type: 'MELEE' },
  { name: 'Paladin', type: 'MELEE' },
  { name: 'Amazon', type: 'RANGED' },
  { name: 'Demon Slayer', type: 'RANGED' },
  { name: 'Demonspawn', type: 'MELEE' },
  { name: 'Shaman', type: 'MAGIC' },
  { name: 'White Mage', type: 'MAGIC' },
  { name: 'Marauder', type: 'MELEE' },
  { name: 'Plague Doctor', type: 'MAGIC' },
  { name: 'Shield Lancer', type: 'MELEE' },
  { name: 'Jötunn', type: 'MELEE' },
  { name: 'Illusionist', type: 'MAGIC' },
  { name: 'Exo', type: 'MELEE' },
  { name: 'Butcher', type: 'MELEE' },
  { name: 'Stormweaver', type: 'MAGIC' },
  { name: 'Bard', type: 'MAGIC' },
];

const EXTRA_SHIELDS = [
  { id: 'archmagus-glyphward', name: 'Archmagus Glyphward', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Archmagus_Glyphward.png', data: { Tier: 'C', Level: 20, Resumo: 'Energy & Intelligence' } },
  { id: 'battle-mages-shield', name: "Battle Mage's Shield", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Battle_Mages_Shield.png', data: { Tier: 'A', Level: 58, Resumo: '+1 All Skills & FCR' } },
  { id: 'battlemagis-rampart', name: "Battlemagi's Rampart", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Battlemagis_Rampart.png', data: { Tier: 'D', Level: 16, Resumo: 'Defense & Replenish' } },
  { id: 'blessed-defender', name: 'Blessed Defender', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Blessed_Defender.png', data: { Tier: 'C', Level: 28, Resumo: 'Lightning DMG & Blocking' } },
  { id: 'bobs-piece-of-plywood', name: "Bob's Piece of Plywood", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Bobs_Piece_of_Plywood.png', data: { Tier: 'C', Level: 25, Resumo: 'Chainsaw Massacre Proc' } },
  { id: 'crystal-infused-shield', name: 'Crystal Infused Shield', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Crystal_Infused_Shield.png', data: { Tier: 'B', Level: 52, Resumo: '+1 All Skills & Resists' } },
  { id: 'darkwood-defender', name: 'Darkwood Defender', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Darkwood_Defender.png', data: { Tier: 'C', Level: 23, Resumo: 'Str/Energy/Vit' } },
  { id: 'den-glider-in', name: 'Den Glider In', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Den_Glider_In.png', data: { Tier: 'A', Level: 57, Resumo: 'Magic Find & Vitality' } },
  { id: 'gem-encrusted-tower', name: 'Gem Encrusted Tower', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Gem_Encrusted_Tower.png', data: { Tier: 'S', Level: 93, Resumo: 'Defense & Sockets' } },
  { id: 'heavens-champion', name: "Heaven's Champion", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Heavens_Champion.png', data: { Tier: 'S', Level: 82, Resumo: '+2-4 All Skills' } },
  { id: 'iron-giants-shield', name: "Iron Giant's Shield", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Iron_Giants_Shield.png', data: { Tier: 'S', Level: 90, Resumo: 'Massive Defense' } },
  { id: 'kings-tower-shield', name: "King's Tower Shield", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Kings_Tower_Shield.png', data: { Tier: 'S', Level: 83, Resumo: 'All Attributes & Reduction' } },
  { id: 'lancers-wall', name: "Lancer's Wall", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Lancers_Wall.png', data: { Tier: 'A', Level: 55, Resumo: 'Phys/Magic Reduction' } },
  { id: 'leprechauns-greed', name: "Leprechaun's Greed", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Leprechauns_Greed.png', data: { Tier: 'S', Level: 87, Resumo: 'Gold Find & Greed' } },
  { id: 'lost-wizards-barrier', name: "Lost Wizard's Barrier", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Lost_Wizards_Barrier.png', data: { Tier: 'C', Level: 30, Resumo: 'Arcane DMG & CDR' } },
  { id: 'mirror-of-flesh', name: 'Mirror of Flesh', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Mirror_of_Flesh.png', data: { Tier: 'S', Level: 88, Resumo: 'Life Steal & Vitality' } },
  { id: 'novices-tower', name: "Novice's Tower", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Novices_Tower.png', data: { Tier: 'S', Level: 92, Resumo: 'Beginner High Tier' } },
  { id: 'pandemic-shield', name: 'Pandemic Shield', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Pandemic_Shield.png', data: { Tier: 'B', Level: 50, Resumo: 'Poison Nova Proc' } },
  { id: 'pitfiends-thorn', name: "Pitfiend's Thorn", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Pitfiends_Thorn.png', data: { Tier: 'S', Level: 87, Resumo: 'Thorns & Defense' } },
  { id: 'rendguard-of-carnage', name: 'Rendguard of Carnage', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Rendguard_of_Carnage.png', data: { Tier: 'B', Level: 40, Resumo: 'Crit & Deadly Blow' } },
  { id: 'rift-eye', name: 'Rift Eye', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Rift_Eye.png', data: { Tier: 'A', Level: 64, Resumo: 'Arcane Skills & FCR' } },
  { id: 'rusted-spiked-shield', name: 'Rusted Spiked Shield', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Rusted_Spiked_Shield.png', data: { Tier: 'C', Level: 26, Resumo: 'Fan of Knives Proc' } },
  { id: 'shadow-buckler', name: 'Shadow Buckler', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Shadow_Buckler.png', data: { Tier: 'S', Level: 88, Resumo: 'Evasion & Darkness' } },
  { id: 'shield-of-shattered-elements', name: 'Shield of Shattered Elements', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Shield_of_Shattered_Elements.png', data: { Tier: 'D', Level: 14, Resumo: 'Enemy Resists Lower' } },
  { id: 'spellbinders-magebarrier', name: "Spellbinder's Magebarrier", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Spellbinders_Magebarrier.png', data: { Tier: 'B', Level: 35, Resumo: 'Magic DMG & Mana' } },
  { id: 'storm-gladiators-wall', name: "Storm Gladiator's Wall", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Storm_Gladiators_Wall.png', data: { Tier: 'B', Level: 54, Resumo: 'Lightning Fury Proc' } },
  { id: 'the-templar', name: 'The Templar', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_The_Templar.png', data: { Tier: 'A', Level: 61, Resumo: '+2 Skills & Movement' } },
  { id: 'thunder-lords-aegis', name: "Thunder Lord's Aegis", rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Thunder_Lords_Aegis.png', data: { Tier: 'S', Level: 88, Resumo: 'High Lightning DMG' } },
  { id: 'thunderburn-aegis', name: 'Thunderburn Aegis', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Thunderburn_Aegis.png', data: { Tier: 'D', Level: 12, Resumo: 'Fire/Light DMG' } },
  { id: 'thunderstruck-deflector', name: 'Thunderstruck Deflector', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Thunderstruck_Deflector.png', data: { Tier: 'B', Level: 45, Resumo: 'Movement & IAS' } },
  { id: 'visage-of-relentless-rage', name: 'Visage of Relentless Rage', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Visage_of_Relentless_Rage.png', data: { Tier: 'S', Level: 86, Resumo: 'Physical DMG & Strength' } },
  { id: 'wall-of-the-legion', name: 'Wall of the Legion', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Wall_of_the_Legion.png', data: { Tier: 'S', Level: 93, Resumo: 'Minion/Ally Buffs' } },
  { id: 'ward-of-blood', name: 'Ward of Blood', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Ward_of_Blood.png', data: { Tier: 'S', Level: 86, Resumo: 'Life Steal & Devout Doctor' } },
  { id: 'ward-of-toxicity', name: 'Ward of Toxicity', rarity: 'Satanic', image: 'https://herosiege.wiki.gg/images/Shields_Ward_of_Toxicity.png', data: { Tier: 'B', Level: 48, Resumo: 'Poison Skill DMG' } },
  { id: 'aztec-ward', name: 'Aztec Ward', rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Aztec_Ward.png', data: { Tier: 'A', Level: 52, Resumo: '+2 Poison Skills, FCR, Poison DMG' } },
  { id: 'flame-diviners-ward', name: "Flame Diviner's Ward", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Flame_Diviners_Ward.png', data: { Tier: 'A', Level: 60, Resumo: 'Fire Skill DMG, FCR, Fire Resist' } },
  { id: 'gladiators-wall-set', name: "Gladiator's Wall", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Gladiators_Wall.png', data: { Tier: 'A', Level: 48, Resumo: 'Crit Chance/DMG, Mana Steal, Str/Dex' } },
  { id: 'justiciars-thunder-wall', name: "Justiciar's Thunder Wall", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Justiciars_Thunder_Wall.png', data: { Tier: 'A', Level: 59, Resumo: 'Lightning Skill DMG, DMG Reduction' } },
  { id: 'sacred-aegis', name: 'Sacred Aegis', rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Sacred_Aegis.png', data: { Tier: 'A', Level: 53, Resumo: 'Attack Rating, Mana Steal, Life based on Level' } },
  { id: 'shield-bearers-wall', name: "Shield Bearer's Wall", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Shield_Bearers_Wall.png', data: { Tier: 'A', Level: 65, Resumo: 'Attack DMG, Life Steal, Str, All Resist' } },
  { id: 'vagabonds-ward', name: "Vagabond's Ward", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Vagabonds_Ward.png', data: { Tier: 'A', Level: 51, Resumo: '+2 Physical Skills, IAS, All Attributes, Resist' } },
  { id: 'valkyries-battle-shield', name: "Valkyrie's Battle Shield", rarity: 'Satanic Set', image: 'https://herosiege.wiki.gg/images/Shields_Valkyries_Battle_Shield.png', data: { Tier: 'A', Level: 56, Resumo: 'Blocking Chance, FCR, Lightning Skill DMG' } },
  { id: 'ancient-aegis', name: 'Ancient Aegis', rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Ancient_Aegis.png', data: { Tier: 'SS', Level: 95, Resumo: 'Ancient Power' } },
  { id: 'celestials-authority', name: "Celestial's Authority", rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Celestials_Authority.png', data: { Tier: 'SS', Level: 95, Resumo: 'Holy Protection' } },
  { id: 'defenders-justice', name: "Defender's Justice", rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Defenders_Justice.png', data: { Tier: 'SS', Level: 98, Resumo: 'True Tank' } },
  { id: 'demons-mirror', name: "Demon's Mirror", rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Demons_Mirror.png', data: { Tier: 'SS', Level: 92, Resumo: 'Reflection' } },
  { id: 'pit-ravagers-cetratus', name: "Pit Ravager's Cetratus", rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Pit_Ravagers_Cetratus.png', data: { Tier: 'SS', Level: 92, Resumo: 'Arena Legend' } },
  { id: 'shield-of-the-abyss', name: 'Shield of the Abyss', rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Shield_of_the_Abyss.png', data: { Tier: 'SS', Level: 96, Resumo: 'Void Barrier' } },
  { id: 'suomi-finland-perkele', name: 'SUOMI FINLAND PERKELE', rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_SUOMI_FINLAND_PERKELE.png', data: { Tier: 'SS', Level: 92, Resumo: 'Nordic Rage' } },
  { id: 'tombstone-shield', name: 'Tombstone', rarity: 'Heroic', image: 'https://herosiege.wiki.gg/images/Shields_Tombstone.png', data: { Tier: 'SS', Level: 100, Resumo: 'Undeath' } },
  { id: 'gabriels-unholy-oath', name: "Gabriel's Unholy Oath", rarity: 'Unholy', image: 'https://herosiege.wiki.gg/images/Shields_Gabriels_Unholy_Oath.png', data: { Tier: 'SS', Level: 100, Resumo: 'Summoning & Curse' } },
  { id: 'st-hallgars-aegis', name: "St. Hallgar's Aegis", rarity: 'Angelic', image: 'https://herosiege.wiki.gg/images/Shields_St._Hallgars_Bloodforged_Aegis.png', data: { Tier: 'SS', Level: 100, Resumo: 'Blood & Light' } },
  { id: 'st-tomis-vibrant-aura', name: "St. Tomi's Vibrant Aura", rarity: 'Angelic', image: 'https://herosiege.wiki.gg/images/Shields_St._Tomis_Vibrant_Aura.png', data: { Tier: 'SS', Level: 100, Resumo: 'Holy Auras' } }
];

const SOCKET_ITEMS = [
  // Runes
  ...['Old', 'Ol', 'Tor', 'Naf', 'Uth', 'Eth', 'Tul', 'Rex', 'Ert', 'Thal', 'Ymn', 'Nut', 'Del', 'Hel', 'Io', 'Lum', 'Co', 'Fel', 'Lem', 'Pul', 'Um', 'Mal', 'Ist', 'Gul', 'Vex', 'Qi', 'Xo', 'Sur', 'Ber', 'Jah', 'Drax', 'Zed', 'Fawn', 'Flo', 'Nju', 'Jol'].map(name => ({
    name,
    type: 'Rune',
    category: 'Rune',
    img: `https://herosiege.wiki.gg/images/Rune_${name}.png`
  })),
  // Gems
  { name: 'Pristine Ruby', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/3/3f/Pristine_ruby.png' },
  { name: 'Pristine Sapphire', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/a/a2/Pristine_sapphire.png' },
  { name: 'Pristine Topaz', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/0/0a/Pristine_topaz.png' },
  { name: 'Pristine Emerald', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/f/f0/Pristine_emerald.png' },
  { name: 'Pristine Amethyst', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/0/08/Pristine_amethyst.png' },
  { name: 'Pristine Citrine', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/4/4b/Pristine_citrine.png' },
  { name: 'Pristine Skull', type: 'Gem', category: 'Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/6/64/Pristine_skull.png' },
  { name: 'Elemental Gem', type: 'Gem', category: 'Unique Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/5/50/Elemental_Gem.gif' },
  { name: 'Chaos Gem', type: 'Gem', category: 'Unique Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/1/12/Chaos_Gem.gif' },
  { name: 'Angelic Gem', type: 'Gem', category: 'Unique Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/f/f7/Angelic_gem.gif' },
  { name: 'Moonstone Gem', type: 'Gem', category: 'Unique Gem', img: 'https://static.wikia.nocookie.net/herosiege/images/2/2a/Moonstone.gif' },
  // Jewels
  { name: 'Exan Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/c/c0/Jewel_Exan_spr.png' },
  { name: 'Wildren Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/b/bf/Jewel_Wilrden_spr.png' },
  { name: 'Volcon Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/7/7b/Jewel_Volcon_spr.png' },
  { name: 'Aether Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/d/dd/Jewel_Aether_spr.png' },
  { name: 'Helmon Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/3/3a/Jewel_Helmon_spr.png' },
  { name: 'Mariane Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/7/7c/Jewel_Mariane_spr.png' },
  { name: 'Lyrcon Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/d/dd/Jewel_Lyrcon_spr.png' },
  { name: 'Fieryzen Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/0/05/Jewel_Fieryzen_spr.png' },
  { name: 'Lapis-Lazuli Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/1/14/Lapis.gif' },
  { name: 'Omnipearl Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/b/b0/Omnipearl.gif' },
  { name: 'Agathetheum Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/f/f7/Agathetheum.gif' },
  { name: 'Tramal Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/f/f2/Tramal.gif' },
  { name: 'Pearlescento Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/4/4b/Pearlescento.gif' },
  { name: 'Mythgonlion Jewel', type: 'Jewel', category: 'Jewel', img: 'https://static.wikia.nocookie.net/herosiege/images/c/c2/Mythgonlion.gif' },
];

const NewDesign = ({ onBack, initialView = 'home' }) => {
  const navigate = useNavigate();
  const { postId, buildId } = useParams();
  const [currentView, setCurrentView] = useState((initialView === 'build' ? 'home' : initialView) || 'home');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [isDbOpen, setIsDbOpen] = useState(false);
  const dbMenuRef = useRef(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const builderMenuRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dbMenuRef.current && !dbMenuRef.current.contains(e.target)) {
        setIsDbOpen(false);
      }
      if (builderMenuRef.current && !builderMenuRef.current.contains(e.target)) {
        setIsBuilderOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const init = () => {
      if (!window.Twitch || !window.Twitch.Embed) return;
      if (window.__hsbTwitchInitialized) return;
      window.__hsbTwitchInitialized = true;
      const container = document.getElementById(TWITCH_CONTAINER_ID);
      if (!container) return;
      container.innerHTML = '';
      const parentHost = window.location.hostname || 'herosiegebrasil.com.br';
      const parent = ['herosiegebrasil.com.br', 'www.herosiegebrasil.com.br'];
      if (!parent.includes(parentHost)) parent.push(parentHost);
      try {
        const embed = new window.Twitch.Embed(TWITCH_CONTAINER_ID, {
          width: '100%',
          height: '100%',
          channel: 'spacezonetv',
          parent,
          layout: 'video',
          autoplay: true,
          muted: false,
        });
        embed.addEventListener(window.Twitch.Embed.VIDEO_READY, () => {
          const player = embed.getPlayer();
          if (!player) {
            setTwitchOnline(false);
            return;
          }
          if (typeof player.addEventListener === 'function') {
            try {
              player.addEventListener(window.Twitch.Player.PLAY, () => setTwitchOnline(true));
              player.addEventListener(window.Twitch.Player.OFFLINE, () => setTwitchOnline(false));
            } catch {
            }
          } else {
            setTwitchOnline(false);
          }
          try {
            if (typeof player.getPaused === 'function') {
              setTwitchOnline(!player.getPaused());
            }
          } catch {
          }
        });
      } catch {
      }
    };
    const existing = document.querySelector('script[src="https://embed.twitch.tv/embed/v1.js"]');
    if (existing) {
      if (window.Twitch && window.Twitch.Embed) init();
      else existing.addEventListener('load', init, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://embed.twitch.tv/embed/v1.js';
    script.async = true;
    script.addEventListener('load', init, { once: true });
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    setIsDbOpen(false);
  }, [currentView]);

  const filteredClasses = activeFilter === 'ALL' 
    ? CLASS_DATA 
    : CLASS_DATA.filter(c => c.type === activeFilter);

  const [selectedClass, setSelectedClass] = useState(null);
  const [wikiData, setWikiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [itemCategories, setItemCategories] = useState([]);
  const [selectedItemCategory, setSelectedItemCategory] = useState(null);
  const [itemsList, setItemsList] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedBlogPost, setSelectedBlogPost] = useState(null);
  const postsUnsubRef = useRef(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('Contato');
  const [contactMessage, setContactMessage] = useState('');
  const [contactImageUrl, setContactImageUrl] = useState('');
  const [contactCaptchaAnswer, setContactCaptchaAnswer] = useState('');
  const [contactCaptchaA, setContactCaptchaA] = useState(0);
  const [contactCaptchaB, setContactCaptchaB] = useState(0);
  const [contactCaptchaOp, setContactCaptchaOp] = useState('+');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');
  const [tiers, setTiers] = useState({ S: [], A: [], B: [], C: [], D: [], E: [] });
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [forumSelectedClass, setForumSelectedClass] = useState(null);
  const [buildCountsByForum, setBuildCountsByForum] = useState({});
  const [buildsByForum, setBuildsByForum] = useState({});
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [buildModalOpen, setBuildModalOpen] = useState(false);
  const [newBuildOpen, setNewBuildOpen] = useState(false);
  const [twitchOnline, setTwitchOnline] = useState(null);
  const [nbTitle, setNbTitle] = useState('');
  const [nbAuthor, setNbAuthor] = useState('');
  const [nbClass, setNbClass] = useState('');
  const [nbType, setNbType] = useState('iniciante');
  const [nbContent, setNbContent] = useState('');
  const nbContentRef = useRef(null);
  const [nbStats, setNbStats] = useState({
    strength: 0,
    dexterity: 0,
    intelligence: 0,
    energy: 0,
    armor: 0,
    vitality: 0,
  });
  const NB_TOTAL = 400;
  const [nbRelics, setNbRelics] = useState([null, null, null, null, null]);
  const [nbRelicPickerIndex, setNbRelicPickerIndex] = useState(null);
  const [nbPotions, setNbPotions] = useState([null, null, null, null]);
  const [nbPotionPickerIndex, setNbPotionPickerIndex] = useState(null);
  const [nbCharms, setNbCharms] = useState([]);
  const [nbCharmPickerIndex, setNbCharmPickerIndex] = useState(null);
  
  // Weapons & Body Armors
  const [nbWeapons, setNbWeapons] = useState([null, null, null, null]);
  const [nbWeaponPickerIndex, setNbWeaponPickerIndex] = useState(null);
  const [weaponOptions, setWeaponOptions] = useState([]);
  const [nbBodyArmors, setNbBodyArmors] = useState([null, null, null, null]);
  const [nbBodyArmorPickerIndex, setNbBodyArmorPickerIndex] = useState(null);
  const [bodyArmorOptions, setBodyArmorOptions] = useState([]);

  // Augments
  const [nbAugments, setNbAugments] = useState([null]);
  const [nbAugmentPickerIndex, setNbAugmentPickerIndex] = useState(null);

  
  // New Categories
  const [nbBoots, setNbBoots] = useState([null, null, null, null]);
  const [nbBootPickerIndex, setNbBootPickerIndex] = useState(null);
  const [bootOptions, setBootOptions] = useState([]);

  const [nbGloves, setNbGloves] = useState([null, null, null, null]);
  const [nbGlovePickerIndex, setNbGlovePickerIndex] = useState(null);
  const [gloveOptions, setGloveOptions] = useState([]);

  const [nbHelmets, setNbHelmets] = useState([null, null, null, null]);
  const [nbHelmetPickerIndex, setNbHelmetPickerIndex] = useState(null);
  const [helmetOptions, setHelmetOptions] = useState([]);

  const [nbShields, setNbShields] = useState([null, null, null, null]);
  const [nbShieldPickerIndex, setNbShieldPickerIndex] = useState(null);
  const [shieldOptions, setShieldOptions] = useState([]);

  const [nbAmulets, setNbAmulets] = useState([null, null, null, null]);
  const [nbAmuletPickerIndex, setNbAmuletPickerIndex] = useState(null);
  const [amuletOptions, setAmuletOptions] = useState([]);

  const [nbBelts, setNbBelts] = useState([null, null, null, null]);
  const [nbBeltPickerIndex, setNbBeltPickerIndex] = useState(null);
  const [beltOptions, setBeltOptions] = useState([]);

  const [nbRings, setNbRings] = useState([null, null, null, null]);
  const [nbRingPickerIndex, setNbRingPickerIndex] = useState(null);
  const [ringOptions, setRingOptions] = useState([]);

  const [nbSockets, setNbSockets] = useState([null, null, null, null, null, null, null, null, null, null]);
  const [nbSocketPickerIndex, setNbSocketPickerIndex] = useState(null);

  const [loadingBuildItems, setLoadingBuildItems] = useState(false);

  const [nbMercenary, setNbMercenary] = useState('');
  const [potionOptions, setPotionOptions] = useState([]);
  const [homepageMode, setHomepageMode] = useState('fixed');
  const [homepageFeaturedClass, setHomepageFeaturedClass] = useState(null);
  const [homepageRandomIndex, setHomepageRandomIndex] = useState(() => {
    // If there is a Prophet class (new), we want it to be the first one shown when in random mode
    const prophetIndex = CLASS_DATA.findIndex(c => c.name === 'Prophet');
    if (prophetIndex >= 0) return prophetIndex;
    return CLASS_DATA.length ? Math.floor(Math.random() * CLASS_DATA.length) : 0;
  });
  const [homepageRotationSeconds, setHomepageRotationSeconds] = useState(15);

  const relicOptions = useMemo(() => {
    const base = [...PASSIVE_RELICS, ...EXTRA_RELICS];
    const seen = new Set();
    const merged = [];
    for (const r of base) {
      const nm = r?.name;
      if (!nm || seen.has(nm)) continue;
      seen.add(nm);
      merged.push({ name: nm, img: relicImageFor(r) });
    }
    merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return merged;
  }, []);

  useEffect(() => {
    const loadPotions = async () => {
      try {
        const colRef = collection(db, 'item_categories', 'potions', 'items');
        const snap = await getDocs(colRef);
        const list = [];
        for (const s of snap.docs) {
          list.push({ id: s.id, ...s.data() });
        }
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPotionOptions(list);
      } catch (e) {
        console.error('Erro carregando poções', e);
        setPotionOptions([]);
      }
    };
    loadPotions();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    let title = 'Hero Siege Brasil | Wiki, Itens e Builds em Português';
    let path = '/';

    if (buildId || (selectedBuild && buildModalOpen)) {
      const b = selectedBuild || {};
      if (b.title) {
        title = `${b.title} | Build | Hero Siege Brasil`;
      } else {
        title = 'Build | Hero Siege Brasil';
      }
      const id = buildId || b.id;
      if (id) path = `/build/${encodeURIComponent(id)}`;
    } else if (currentView === 'classes') {
      title = 'Classes | Hero Siege Brasil';
      path = '/classes';
    } else if (currentView === 'items') {
      title = 'Itens | Hero Siege Brasil';
      path = '/items';
    } else if (currentView === 'runes') {
      title = 'Runas | Hero Siege Brasil';
      path = '/runes';
    } else if (currentView === 'relics') {
      title = 'Relíquias | Hero Siege Brasil';
      path = '/relics';
    } else if (currentView === 'chaosTower') {
      title = 'Chaos Tower | Hero Siege Brasil';
      path = '/chaos-tower';
    } else if (currentView === 'mercenaries') {
      title = 'Mercenários | Hero Siege Brasil';
      path = '/mercenarios';
    } else if (currentView === 'mining') {
      title = 'Mineração | Hero Siege Brasil';
      path = '/mineracao';
    } else if (currentView === 'keys') {
      title = 'Chaves | Hero Siege Brasil';
      path = '/chaves';
    } else if (currentView === 'quests') {
      title = 'Quests | Hero Siege Brasil';
      path = '/quests';
    } else if (currentView === 'gems') {
      title = 'Gemas e Jóias | Hero Siege Brasil';
      path = '/gems';
    } else if (currentView === 'charms') {
      title = 'Charms | Hero Siege Brasil';
      path = '/charms';
    } else if (currentView === 'builder') {
      if (forumSelectedClass) {
        title = `Forum ${forumSelectedClass} | Hero Siege Brasil`;
      } else {
        title = 'Forum Builds | Hero Siege Brasil';
      }
      path = '/forum';
    } else if (currentView === 'contact') {
      title = 'Contato | Hero Siege Brasil';
      path = '/contato';
    } else if (currentView === 'blog') {
      if (selectedBlogPost && selectedBlogPost.title) {
        title = `${selectedBlogPost.title} | Blog | Hero Siege Brasil`;
      } else {
        title = 'Blog | Hero Siege Brasil';
      }
      const id = postId || (selectedBlogPost && selectedBlogPost.id);
      if (id) path = `/blog/${encodeURIComponent(id)}`;
      else path = '/blog';
    }
    
    const origin = window.location.origin || 'https://herosiegebrasil.com.br';
    const href = origin + path;
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
    document.title = title;
  }, [currentView, forumSelectedClass, selectedBlogPost, postId, buildId, selectedBuild, buildModalOpen]);

  const specTitleFor = (cls, idx) => {
    const n = (cls || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_')
      .trim();
    const map = {
      viking: ['Berserker', 'Shield Bearer'],
      pyromancer: ['Fire Mage', 'Burning Soul'],
      marksman: ['Hunter', 'Marksman'],
      pirate: ['Shipmaster', 'Cannoneer'],
      nomad: ['Sand Walker', 'Desert Blade'],
      redneck: ['Hillbilly', 'Moonshiner'],
      necromancer: ['Summoner', 'Lich'],
      samurai: ['Bushido', 'Ronin'],
      paladin: ['Holy Knight', 'Crusader'],
      amazon: ['Spear Maiden', 'Huntress'],
      demon_slayer: ['Executioner', 'Inquisitor'],
      demonspawn: ['Hellfire', 'Demonic'],
      shaman: ['Elemental', 'Totemic'],
      white_mage: ['Holy Beam', 'Benediction'],
      marauder: ['Bombardier', 'Wrecker'],
      plague_doctor: ['Infection', 'Medicine'],
      illusionist: ['Phantasm', 'Mirror'],
      exo: ['Gravity', 'Force'],
      butcher: ['Meat', 'Blood'],
      stormweaver: ['Thunder', 'Storm'],
      bard: ['Minstrel', 'Troubadour']
    };
    const arr = map[n];
    if (arr && idx < arr.length) return arr[idx];
    return `Especialização ${idx + 1}`;
  };

  const resetContactCaptcha = () => {
    const a = Math.floor(Math.random() * 10);
    const b = Math.floor(Math.random() * 10);
    const useSub = Math.random() < 0.5;
    if (useSub) {
      const max = Math.max(a, b);
      const min = Math.min(a, b);
      setContactCaptchaA(max);
      setContactCaptchaB(min);
      setContactCaptchaOp('-');
    } else {
      setContactCaptchaA(a);
      setContactCaptchaB(b);
      setContactCaptchaOp('+');
    }
    setContactCaptchaAnswer('');
  };

  useEffect(() => {
    if (currentView === 'contact') {
      resetContactCaptcha();
    }
  }, [currentView]);

  const submitContact = async () => {
    setContactError('');
    setContactSent(false);
    if (!contactName || !contactEmail || !contactMessage || !contactSubject) {
      setContactError('Preencha todos os campos.');
      return;
    }
    const a = contactCaptchaA;
    const b = contactCaptchaB;
    const expected = contactCaptchaOp === '+' ? a + b : a - b;
    const given = parseInt(String(contactCaptchaAnswer).trim(), 10);
    if (Number.isNaN(given) || given !== expected) {
      setContactError('Verificação incorreta. Tente novamente.');
      resetContactCaptcha();
      return;
    }
    try {
      setContactSending(true);
      await addDoc(collection(db, 'messages'), {
        name: contactName,
        email: contactEmail,
        category: contactSubject,
        message: contactMessage,
        imageUrl: contactImageUrl || '',
        read: false,
        createdAt: serverTimestamp()
      });
      setContactName('');
      setContactEmail('');
      setContactSubject('Contato');
      setContactMessage('');
      setContactImageUrl('');
      setContactCaptchaAnswer('');
      resetContactCaptcha();
      setContactSent(true);
    } catch (e) {
      setContactError('Erro ao enviar. Tente novamente.');
    } finally {
      setContactSending(false);
    }
  };

  useEffect(() => {
    const ref = doc(db, 'config', 'tierlist');
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      const next = {
        S: Array.isArray(d.S) ? d.S : [],
        A: Array.isArray(d.A) ? d.A : [],
        B: Array.isArray(d.B) ? d.B : [],
        C: Array.isArray(d.C) ? d.C : [],
        D: Array.isArray(d.D) ? d.D : [],
        E: Array.isArray(d.E) ? d.E : [],
      };
      setTiers(next);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const ref = doc(db, 'config', 'homepage');
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      setHomepageMode(d.mode || 'fixed');
      setHomepageFeaturedClass(d.featuredClass || null);
      if (typeof d.rotationSeconds === 'number' && d.rotationSeconds > 0) {
        setHomepageRotationSeconds(d.rotationSeconds);
      } else {
        setHomepageRotationSeconds(15);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (homepageMode !== 'random') return;
    if (!CLASS_DATA.length) return;
    const computeIndex = () => {
      const intervalMs = Math.max(5, homepageRotationSeconds || 15) * 1000;
      const bucket = Math.floor(Date.now() / intervalMs);
      const idx = bucket % CLASS_DATA.length;
      setHomepageRandomIndex(idx);
    };
    computeIndex();
    const intervalMs = Math.max(5, homepageRotationSeconds || 15) * 1000;
    const id = setInterval(computeIndex, intervalMs);
    return () => clearInterval(id);
  }, [homepageMode, homepageRotationSeconds]);

  const sanitizeAndNormalizeHtml = (html) => {
    if (!html) return '';
    if (typeof html !== 'string') return '';
    return html.replace(/http:/g, 'https:');
  };

  const buildModalContentHtml = useMemo(() => {
    if (!selectedBuild || !selectedBuild.content_html) return '';
    return sanitizeAndNormalizeHtml(selectedBuild.content_html);
  }, [selectedBuild]);

  const heroClassName = useMemo(() => {
    if (homepageMode === 'fixed' && homepageFeaturedClass) return homepageFeaturedClass;
    if (homepageMode === 'random') {
      const fromList = CLASS_DATA[homepageRandomIndex] || CLASS_DATA[0];
      return fromList ? fromList.name : 'Viking';
    }
    return homepageFeaturedClass || 'Viking';
  }, [homepageMode, homepageFeaturedClass, homepageRandomIndex]);

  useEffect(() => {
    const ref = collection(db, 'builds');
    const unsub = onSnapshot(ref, (snap) => {
      const counts = {};
      const byForum = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const status = data.status || 'draft';
        if (status !== 'published') return;
        const baseClass = data.className || data.heroClass || '';
        const forum = data.forum || data.classSlug || (baseClass ? slugifyClass(baseClass) : '');
        if (!forum) return;
        counts[forum] = (counts[forum] || 0) + 1;
        if (!byForum[forum]) byForum[forum] = [];
        byForum[forum].push({ id: docSnap.id, ...data });
      });
      Object.keys(byForum).forEach((f) => {
        byForum[f].sort((a, b) => {
          const at = a.createdAt && typeof a.createdAt.seconds === 'number' ? a.createdAt.seconds : 0;
          const bt = b.createdAt && typeof b.createdAt.seconds === 'number' ? b.createdAt.seconds : 0;
          return bt - at;
        });
      });
      setBuildCountsByForum(counts);
      setBuildsByForum(byForum);
    });
    return () => unsub();
  }, []);

  const imageFor = (name) => {
    const base = String(name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '');
    const pub = process.env.PUBLIC_URL || '';
    const basePath = pub ? `${pub}/images` : `images`;
    return `${basePath}/${base}.webp`;
  };

  const downloadTierImage = async () => {
    const rowKeys = ['S','A','B','C','D','E'];
    const colors = { S:'#dc2626', A:'#f97316', B:'#eab308', C:'#84cc16', D:'#22c55e', E:'#6b7280' };
    const bgColors = { S:'rgba(220,38,38,0.12)', A:'rgba(249,115,22,0.12)', B:'rgba(234,179,8,0.12)', C:'rgba(132,204,22,0.12)', D:'rgba(34,197,94,0.12)', E:'rgba(107,114,128,0.12)' };
    const w = 1024;
    const pad = 24;
    const headerH = 56;
    const labelSize = 48;
    const iconSize = 40;
    const gap = 8;
    const rowGap = 16;
    let height = pad + headerH + pad;
    const rows = [];
    rowKeys.forEach(k => {
      const rowH = Math.max(labelSize, iconSize) + pad;
      rows.push({ key:k, y: height, h: rowH });
      height += rowH + rowGap;
    });
    height += pad + 40;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#151923';
    ctx.fillRect(0, 0, w, height);
    const loadImg = (src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.crossOrigin = 'anonymous';
      img.src = src;
    });
    // Header with logo and title
    const logoBase = (process.env.PUBLIC_URL || '') ? `${process.env.PUBLIC_URL}/images` : `images`;
    const logo = await loadImg(`${logoBase}/herosiege.png`);
    if (logo) {
      const lw = 160;
      const lh = 40;
      const ly = pad + Math.max(0, (headerH - lh) / 2);
      ctx.drawImage(logo, pad, ly, lw, lh);
      ctx.font = 'bold 22px Segoe UI, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('Tier List Completa', pad + lw + 12, pad + headerH / 2);
    }
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(pad, pad + headerH, w - pad * 2, 1); // bottom border of header
    // Rows
    ctx.font = 'bold 20px Segoe UI, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    const tasks = [];
    rows.forEach(row => {
      const k = row.key;
      const y = row.y;
      // Row background (lighter) and left border
      ctx.fillStyle = bgColors[k] || 'rgba(255,255,255,0.06)';
      ctx.fillRect(pad, y, w - pad*2, row.h - rowGap + 4);
      ctx.fillStyle = colors[k] || '#6b7280';
      ctx.fillRect(pad, y, 4, row.h - rowGap + 4);
      // Tier square
      ctx.fillStyle = colors[k] || '#6b7280';
      ctx.fillRect(pad + 8, y + Math.max(0, ((row.h - rowGap) - labelSize)/2), labelSize, labelSize);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(k, pad + 8 + labelSize/2, y + Math.max(0, ((row.h - rowGap) - labelSize)/2) + labelSize/2);
      // Icons
      let x = pad + 8 + labelSize + 16;
      (tiers[k] || []).forEach(cls => {
        const src = imageFor(cls);
        tasks.push({
          x,
          y: y + Math.max(0, ((row.h - rowGap) - iconSize)/2),
          src
        });
        x += iconSize + gap;
      });
    });
    for (let i=0;i<tasks.length;i++) {
      const t = tasks[i];
      // eslint-disable-next-line no-await-in-loop
      const img = await loadImg(t.src);
      if (img) ctx.drawImage(img, t.x, t.y, iconSize, iconSize);
    }
    // Timestamp bottom-right
    const now = new Date();
    const dd = String(now.getDate()).padStart(2,'0');
    const mm = String(now.getMonth()+1).padStart(2,'0');
    const yyyy = String(now.getFullYear());
    const hh = String(now.getHours()).padStart(2,'0');
    const min = String(now.getMinutes()).padStart(2,'0');
    const stamp = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    ctx.font = 'bold 18px Segoe UI, sans-serif';
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#e5e7eb';
    ctx.fillText(stamp, w - pad, height - 12);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `tierlist_${yyyy}${mm}${dd}_${hh}${min}.png`;
    a.click();
  };

  const tierRowStyle = (k) => {
    if (k === 'S') return { backgroundColor: 'rgba(220,38,38,0.12)', borderLeft: '4px solid #dc2626' };
    if (k === 'A') return { backgroundColor: 'rgba(249,115,22,0.12)', borderLeft: '4px solid #f97316' };
    if (k === 'B') return { backgroundColor: 'rgba(234,179,8,0.12)', borderLeft: '4px solid #eab308' };
    if (k === 'C') return { backgroundColor: 'rgba(132,204,22,0.12)', borderLeft: '4px solid #84cc16' };
    if (k === 'D') return { backgroundColor: 'rgba(34,197,94,0.12)', borderLeft: '4px solid #22c55e' };
    return { backgroundColor: 'rgba(107,114,128,0.12)', borderLeft: '4px solid #6b7280' };
  };

  const navigateToView = (view) => {
    setCurrentView(view);
    if (view === 'home') navigate('/');
    else if (view === 'classes') navigate('/classes');
    else if (view === 'items') navigate('/items');
    else if (view === 'runes') navigate('/runes');
    else if (view === 'relics') navigate('/relics');
    else if (view === 'chaosTower') navigate('/chaos-tower');
    else if (view === 'mercenaries') navigate('/mercenarios');
    else if (view === 'mining') navigate('/mineracao');
    else if (view === 'quests') navigate('/quests');
    else if (view === 'charms') navigate('/charms');
    else if (view === 'builder') navigate('/forum');
    else if (view === 'contact') navigate('/contato');
    else if (view === 'blog') navigate('/blog');
  };

  const openBlogPost = (post) => {
    if (!post) return;
    setSelectedBlogPost(post);
    setCurrentView('blog');
    if (post.id) navigate(`/blog/${post.slug || post.id}`);
    else navigate('/blog');
  };

  const [steamPlayers, setSteamPlayers] = useState(null);

  const normalizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url.replace(/^http:/i, 'https:');
  };
  const imageOrFallback = (url) => normalizeImageUrl(url) || '/images/herosiege.png';
  const classImagePath = (name, ext = 'webp') => {
    // Slug antigo para legacy images (sem underscore)
    const legacyBase = String(name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '');

    const pub = process.env.PUBLIC_URL || '';
    const basePath = pub ? `${pub}/images` : `images`;

    // Retornar a imagem padrão da raiz (public/images/nome.webp)
    // conforme solicitado pelo usuário
    if (ext === 'legacy') {
      return `${basePath}/${legacyBase}.png`;
    }
    if (ext === 'legacy_webp') {
      return `${basePath}/${legacyBase}.webp`;
    }

    // Default para a imagem da classe na raiz
    return `${basePath}/${legacyBase}.webp`;
  };
  const slugifyClass = (name) =>
    String(name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  const totalNbStats = (stats) =>
    Object.values(stats || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const formatDateTime = (post) => {
    let d = null;
    if (post?.createdAt?.seconds) {
      d = new Date(post.createdAt.seconds * 1000);
    } else if (post?.date) {
      const tryD = new Date(post.date);
      if (!isNaN(tryD.getTime())) d = tryD;
    }
    if (!d) return post?.date || '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const HH = String(d.getHours()).padStart(2, '0');
    const MN = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${HH}:${MN}`;
  };
  const formatBuildTimestamp = (build) => {
    const ts = build?.createdAt;
    let d = null;
    if (ts && typeof ts === 'object' && typeof ts.seconds === 'number') {
      d = new Date(ts.seconds * 1000);
    } else if (typeof ts === 'string') {
      const tryD = new Date(ts);
      if (!Number.isNaN(tryD.getTime())) d = tryD;
    }
    if (!d) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const HH = String(d.getHours()).padStart(2, '0');
    const MN = String(d.getMinutes()).padStart(2, '0');
    const SS = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} • ${HH}:${MN}:${SS}`;
  };
  const contentHtml = useMemo(
    () => sanitizeAndNormalizeHtml(selectedBlogPost?.content_html || ''),
    [selectedBlogPost?.content_html]
  );
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = contentHtml;
      try {
        const imgs = contentRef.current.querySelectorAll('img');
        imgs.forEach((img) => {
          if (img.src.startsWith('http:')) img.src = img.src.replace(/^http:/i, 'https:');
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
         img.onerror = () => { img.src = '/images/herosiege.png'; };
        });
        const ifr = contentRef.current.querySelectorAll('iframe');
        ifr.forEach((f) => {
          if (f.src && f.src.startsWith('http:')) f.src = f.src.replace(/^http:/i, 'https:');
          f.style.width = '100%';
          f.style.height = 'auto';
        });
      } catch {}
    }
  }, [contentHtml]);
  useEffect(() => {
    let stop = false;
    const loadPlayers = async () => {
      try {
        const viaCustom = async () => {
          const u = typeof window !== 'undefined' && window.STEAM_ENDPOINT_URL ? window.STEAM_ENDPOINT_URL : '';
          if (!u) throw new Error('No custom endpoint');
          const r = await fetch(u, { cache: 'no-store' });
          if (!r.ok) throw new Error('Custom endpoint error');
          return r.json();
        };
        const direct = async () => {
          const r = await fetch('https://api.steampowered.com/ISteamUserStats/GetNumberOfCurrentPlayers/v1/?appid=269210', { cache: 'no-store', mode: 'cors' });
          if (!r.ok) throw new Error('Steam API error');
          return r.json();
        };
        const viaProxy = async () => {
          const url = 'https://cors.isomorphic-git.org/https://api.steampowered.com/ISteamUserStats/GetNumberOfCurrentPlayers/v1/?appid=269210';
          const r = await fetch(url, { cache: 'no-store' });
          if (!r.ok) throw new Error('Proxy API error');
          return r.json();
        };
        let j = null;
        try {
          j = await viaCustom();
        } catch {
          try {
            j = await direct();
          } catch {
            j = await viaProxy();
          }
        }
        if (!stop) {
          const count = (j && typeof j.player_count === 'number')
            ? j.player_count
            : (j && j.response && typeof j.response.player_count === 'number')
              ? j.response.player_count
              : null;
          setSteamPlayers(count);
        }
      } catch {
        if (!stop) setSteamPlayers(null);
      }
    };
    loadPlayers();
    const id = setInterval(loadPlayers, 60 * 1000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (currentView !== 'home') return;
    const targetDate = new Date('2026-04-03T00:00:00');
    const updateCountdown = () => {
      const now = new Date();
      const difference = targetDate - now;
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        setTimeLeft({ days, hours, minutes });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [currentView]);

  const loadBlogPosts = async () => {
    try {
      const postsRef = collection(db, 'posts');
      // Prefer server-side filtering and ordering for published posts
      let q;
      try {
        q = query(postsRef, where('status', '==', 'published'), orderBy('createdAt', 'desc'));
      } catch (_) {
        // Fallback without query helpers if something goes wrong
        q = postsRef;
      }
      const snap = await getDocs(q || postsRef);
      const arr = [];
      snap.forEach(s => {
        const d = s.data();
        if (!q) {
          if ((d.status || 'draft') === 'published') {
            arr.push({ id: s.id, ...d });
          }
        } else {
          arr.push({ id: s.id, ...d });
        }
      });
      if (!q) {
        arr.sort((a, b) => {
          const at = (a.createdAt && a.createdAt.seconds) ? a.createdAt.seconds : 0;
          const bt = (b.createdAt && b.createdAt.seconds) ? b.createdAt.seconds : 0;
          return bt - at;
        });
      }
      setBlogPosts(arr);
    } catch (e) {
      console.error('Erro carregando posts do blog', e);
      setBlogPosts([]);
    }
  };

  useEffect(() => {
    if (currentView === 'home' || currentView === 'blog') {
      const postsRef = collection(db, 'posts');
      let q;
      try {
        q = query(postsRef, where('status', '==', 'published'), orderBy('createdAt', 'desc'));
      } catch {
        q = postsRef;
      }
      const unsub = onSnapshot(q, (snap) => {
        const arr = [];
        snap.forEach(s => {
          const d = s.data();
          if (!q) {
            if ((d.status || 'draft') === 'published') arr.push({ id: s.id, ...d });
          } else {
            arr.push({ id: s.id, ...d });
          }
        });
        if (!q) {
          arr.sort((a, b) => {
            const at = (a.createdAt && a.createdAt.seconds) ? a.createdAt.seconds : 0;
            const bt = (b.createdAt && b.createdAt.seconds) ? b.createdAt.seconds : 0;
            return bt - at;
          });
        }
        setBlogPosts(arr);
      }, (err) => {
        console.error('Erro assinando posts do blog', err);
        (async () => {
          try {
            const raw = await getDocs(collection(db, 'posts'));
            const arr = [];
            raw.forEach(s => {
              const d = s.data();
              if ((d.status || 'draft') === 'published') {
                arr.push({ id: s.id, ...d });
              }
            });
            arr.sort((a, b) => {
              const at = (a.createdAt && a.createdAt.seconds) ? a.createdAt.seconds : 0;
              const bt = (b.createdAt && b.createdAt.seconds) ? b.createdAt.seconds : 0;
              return bt - at;
            });
            setBlogPosts(arr);
          } catch (e2) {
            console.error('Fallback de leitura de posts falhou', e2);
            setBlogPosts([]);
          }
        })();
      });
      postsUnsubRef.current = unsub;
      return () => {
        if (postsUnsubRef.current) {
          postsUnsubRef.current();
          postsUnsubRef.current = null;
        }
      };
    } else {
      if (postsUnsubRef.current) {
        postsUnsubRef.current();
        postsUnsubRef.current = null;
      }
    }
  }, [currentView]);

  useEffect(() => {
    const openFromRoute = async () => {
      if (!postId) return;
      if (blogPosts.length === 0) {
        await loadBlogPosts();
      }
      const post = blogPosts.find((p) => p.id === postId || p.slug === postId);
      if (post) {
        setSelectedBlogPost(post);
        setCurrentView('blog');
      }
    };
    openFromRoute();
  }, [postId, blogPosts]);

  useEffect(() => {
    const openFromBuildRoute = async () => {
      if (!buildId) return;
      try {
        let docSnap = null;
        let data = null;
        let finalId = buildId;

        // Tenta buscar por ID direto primeiro (se for um ID válido do Firestore, geralmente 20 chars)
        // Mas como IDs podem variar, tentamos getDoc. Se falhar, tentamos query por slug.
        try {
            const docRef = doc(db, 'builds', buildId);
            docSnap = await getDoc(docRef);
        } catch (e) {
            // Ignora erro de ID inválido, tenta slug
        }

        if (docSnap && docSnap.exists()) {
            data = docSnap.data();
            finalId = docSnap.id;
        } else {
            // Tenta buscar por slug
            const q = query(collection(db, 'builds'), where('slug', '==', buildId));
            const querySnap = await getDocs(q);
            if (!querySnap.empty) {
                const first = querySnap.docs[0];
                data = first.data();
                finalId = first.id;
            }
        }

        if (data) {
           // Aceitamos published e pending (para moderadores verem como fica)
           if (data.status === 'published' || data.status === 'pending') {
             setSelectedBuild({ id: finalId, ...data });
             setBuildModalOpen(true);
           } else {
             console.log('Build não publicada ou rascunho');
             navigate('/');
           }
        } else {
           navigate('/');
        }
      } catch (e) {
        console.error('Erro ao carregar build via URL', e);
        navigate('/');
      }
    };
    openFromBuildRoute();
  }, [buildId, navigate]);

  useEffect(() => {
    if (!newBuildOpen) return;
    if (weaponOptions.length > 0 && bodyArmorOptions.length > 0) return;
    
    const loadBuildItems = async () => {
      setLoadingBuildItems(true);
      try {
        // Carregar Weapons
        const weaponCategories = [
          'swords', 'daggers', 'maces', 'axes', 'claws', 'polearms', 'chainsaws',
          'staves', 'canes', 'wands', 'books', 'spellblades', 'bows', 'guns',
          'throwing weapons'
        ];
        let allWeapons = [];
        for (const cat of weaponCategories) {
          // Tentar com underline se falhar? Normalmente ids sao lowercase e slugified
          // Vamos assumir que os IDs das categorias seguem o padrão slug
          let catId = cat.replace(/\s+/g, '_');
          // Mas populate-items usa ids simples ou slugified. Vamos tentar ambos se necessário.
          // O código existente usa id.toLowerCase().endsWith('s')
          
          // Vamos tentar buscar direto pelo slug esperado
          let colRef = collection(db, 'item_categories', catId, 'items');
          let snap = await getDocs(colRef);
          
          if (snap.empty && cat.includes(' ')) {
             // Tentar com espaço se com underline falhou (improvável mas possível)
             colRef = collection(db, 'item_categories', cat, 'items');
             snap = await getDocs(colRef);
          }
          
          for (const s of snap.docs) {
            allWeapons.push({ id: s.id, category: cat, ...s.data() });
          }
        }
        // Remove duplicates if any
        allWeapons = Array.from(new Map(allWeapons.map(item => [item.id, item])).values());
        allWeapons.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setWeaponOptions(allWeapons);

        // Carregar Body Armors
        // Tentar variações comuns de ID para body armors
        const armorCandidates = ['body_armors', 'body-armors', 'body armors'];
        let allArmors = [];
        
        for (const ac of armorCandidates) {
            const armorColRef = collection(db, 'item_categories', ac, 'items');
            const armorSnap = await getDocs(armorColRef);
            if (!armorSnap.empty) {
                for (const s of armorSnap.docs) {
                    allArmors.push({ id: s.id, category: 'body_armors', ...s.data() });
                }
                break; // Achou, para
            }
        }
        
        allArmors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              setBodyArmorOptions(allArmors);

              // Helper for other categories
              const loadCategoryItems = async (catName) => {
                  const candidates = [
                    catName, 
                    catName + 's', 
                    catName.replace(/s$/, ''),
                    catName.charAt(0).toUpperCase() + catName.slice(1),
                    (catName.charAt(0).toUpperCase() + catName.slice(1)) + 's',
                    (catName.charAt(0).toUpperCase() + catName.slice(1)).replace(/s$/, '')
                  ];
                  let items = [];
                  for (const c of candidates) {
                      const ref = collection(db, 'item_categories', c, 'items');
                      const snap = await getDocs(ref);
                      if (!snap.empty) {
                          for (const s of snap.docs) {
                              items.push({ id: s.id, category: catName, ...s.data() });
                          }
                          break;
                      }
                  }
                  items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  return items;
              };

              setBootOptions(await loadCategoryItems('boots'));
              setGloveOptions(await loadCategoryItems('gloves'));
              setHelmetOptions(await loadCategoryItems('helmets'));
              
              let loadedShields = [];
              try {
                loadedShields = await loadCategoryItems('shields');
              } catch (err) {
                console.warn('Failed to load shields from DB, using fallback', err);
              }

              // Se não encontrou nada no banco ou encontrou muito poucos (possível erro de carga), 
              // mistura com os shields manuais para garantir que apareçam
              if (!loadedShields || loadedShields.length < 5) {
                 console.log('Merging EXTRA_SHIELDS fallback due to low/no DB results');
                 if (typeof EXTRA_SHIELDS !== 'undefined' && EXTRA_SHIELDS.length > 0) {
                    const extra = EXTRA_SHIELDS.map(s => ({...s, img: s.image, category: 'Shields'}));
                    
                    // Se loadedShields é nulo/indefinido, inicializa
                    const currentList = loadedShields || [];
                    const existingNames = new Set(currentList.map(s => String(s.name || '').trim().toLowerCase()));
                    
                    const toAdd = extra.filter(s => !existingNames.has(String(s.name || '').trim().toLowerCase()));
                    loadedShields = [...currentList, ...toAdd];
                 }
              }
              // Ordenar por nome
              loadedShields.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              
              setShieldOptions(loadedShields);

              setAmuletOptions(await loadCategoryItems('amulets'));
              setBeltOptions(await loadCategoryItems('belts'));
              setRingOptions(await loadCategoryItems('rings'));
              
            } catch (e) {
        console.error("Erro ao carregar itens para build:", e);
      } finally {
        setLoadingBuildItems(false);
      }
    };
    
    loadBuildItems();
  }, [newBuildOpen, weaponOptions.length, bodyArmorOptions.length]);

  const loadItemCategories = async () => {
    setItemsLoading(true);
    try {
      const colRef = collection(db, 'item_categories');
      const snap = await getDocs(colRef);
      const cats = [];
      for (const s of snap.docs) {
        cats.push({ id: s.id, ...s.data() });
      }
      cats.sort((a, b) => {
        const ao = a.order ?? 999;
        const bo = b.order ?? 999;
        if (ao !== bo) return ao - bo;
        return (a.title || '').localeCompare(b.title || '');
      });
      setItemCategories(cats);
    } catch (e) {
      console.error('Erro carregando categorias de itens', e);
      setItemCategories([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const loadItemsForCategory = async (cat) => {
    setItemsLoading(true);
    setSelectedItemCategory(cat);
    try {
      const candidates = (() => {
        const id = String(cat.id || '').trim();
        const arr = [id];
        if (id.toLowerCase().endsWith('s')) arr.push(id.slice(0, -1));
        else arr.push(id + 's');
        return Array.from(new Set(arr.map(s => s.toLowerCase())));
      })();

      let pickedItems = [];
      for (const cid of candidates) {
        const colRef = collection(db, 'item_categories', cid, 'items');
        const snap = await getDocs(colRef);
        const items = [];
        for (const s of snap.docs) {
          items.push({ id: s.id, ...s.data() });
        }
        if (items.length > 0) {
          pickedItems = items;
          break;
        }
      }

      const catIdLc = String(cat.id || '').trim().toLowerCase();
      const catTitleLc = String(cat.title || '').trim().toLowerCase();
      const cleaned = pickedItems.filter(it => {
        const n = String(it.name || '').trim().toLowerCase();
        const idn = String(it.id || '').trim().toLowerCase();
        if (!n && idn === catIdLc) return false;
        if (n && (n === catIdLc || n === 'shield')) return false;
        return true;
      });

      const isShieldCategory =
        catIdLc === 'shield' ||
        catIdLc === 'shields' ||
        catTitleLc === 'shield' ||
        catTitleLc === 'shields';

      let finalList = cleaned;

      if (isShieldCategory) {
        const existingNames = new Set(
          cleaned
            .map((it) => String(it.name || '').trim().toLowerCase())
            .filter((n) => n)
        );

        const extraShields = EXTRA_SHIELDS.filter(
          (s) => !existingNames.has(String(s.name || '').trim().toLowerCase())
        );

        if (extraShields.length > 0) {
          finalList = [...cleaned, ...extraShields];
        }
      }

      finalList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setItemsList(finalList);
    } catch (e) {
      console.error('Erro carregando itens da categoria', cat.id, e);
      setItemsList([]);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    if (currentView === 'items') {
      setSelectedItemCategory(null);
      setSelectedItem(null);
      setItemsList([]);
      loadItemCategories();
    }
  }, [currentView]);

  const fetchClassData = async (nome) => {
    setLoading(true);
    setSelectedClass(nome);
    setWikiData(null);
    setActiveTab(0);
    
    const baseId = nome.toLowerCase().replace(/\s+/g, '-').replace(/ö/g, 'o');
    const alternates = [];
    if (baseId === 'bard') alternates.push('monk');

    const tryIds = [baseId, ...alternates];

    // Helper para substituir imagens da Wiki por locais
    const processHtmlWithLocalImages = (html, className) => {
      if (!html) return html;
      const classFolder = className.toLowerCase().replace(/\s+/g, '_'); // Ex: demon_slayer
      return html.replace(/src="(https:\/\/herosiege\.wiki\.gg\/images\/([^"]+))"/g, (match, fullUrl, filename) => {
         let localName = filename;
         // Ajuste específico para Bard: Wiki usa Monk_, Local usa Bard_
         if (classFolder === 'bard' && filename.startsWith('Monk_')) {
             localName = filename.replace('Monk_', 'Bard_');
         }
         return `src="/images/${classFolder}/${localName}" onerror="this.onerror=null;this.src='${fullUrl}'"`;
      });
    };

    try {
      let data = null;
      for (const id of tryIds) {
        const docRef = doc(db, 'classes', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          data = docSnap.data();
          break;
        }
      }

      if (data) {
        let mainSections = data.especializacoes || [{ title: "Geral", html: "<div>Sem dados de especialização.</div>" }];
        
        // Processar imagens nas seções carregadas do banco
        mainSections = mainSections.map(section => ({
            ...section,
            html: processHtmlWithLocalImages(section.html, nome)
        }));

        const extraSections = (data.extra_info || []).map(s => ({ 
            ...s, 
            isExtra: true,
            html: processHtmlWithLocalImages(s.html, nome)
        }));

        if (nome === 'Bard') {
          const chest = '/images/herosiege.png';
          // URLs da Wiki
          const iconPrimaryWiki = (base) => `https://herosiege.wiki.gg/images/Monk_${base}.png`;
          const iconAltWiki = (base) => `https://herosiege.wiki.gg/images/Icon_Monk_${base}.png`;
          
          // URLs Locais
          const iconPrimaryLocal = (base) => `/images/bard/Bard_${base}.png`;
          
          const row = (n, r, l) => {
            const base = String(n).trim().replace(/['’!]/g, '').replace(/\s+/g, '_');
            // Tenta carregar local -> fallback para Wiki Primary -> fallback para Wiki Alt -> fallback para Chest
            const imgTag = `<img src="${iconPrimaryLocal(base)}" alt="${n}" 
                onerror="this.onerror=function(){
                    this.onerror=function(){
                        this.onerror=null;
                        this.src='${chest}'
                    };
                    this.src='${iconAltWiki(base)}'
                };
                this.src='${iconPrimaryWiki(base)}'"
            />`;
            
            return `
              <tr>
                <td>${imgTag}</td>
                <td>${n}</td>
                <td>${r}</td>
                <td>${l}</td>
              </tr>`;
          };
          const tableWrap = (rows) => `
            <table>
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Name</th>
                  <th>Required Skill(s)</th>
                  <th>Required Level</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>`;
          const minstrel = tableWrap([
            row('Melodic Chord', 'None', '1'),
            row('Harmony', 'None', '1'),
            row('Sound Wave', 'Melodic Chord', '6'),
            row('Discord', 'None', '6'),
            row('Vibrant Echo', 'Discord', '12'),
            row('Rhythm', 'Harmony', '12'),
            row('Symphony', 'Sound Wave', '18'),
            row('Resonance', 'Vibrant Echo', '18'),
            row('Masterpiece', 'Rhythm', '24')
          ].join(''));
          const troubadour = tableWrap([
            row('Inspiring Tune', 'None', '1'),
            row('Dancing Step', 'None', '1'),
            row('Lullaby', 'None', '6'),
            row('Power Ballad', 'Dancing Step', '6'),
            row('Chorus', 'Lullaby', '12'),
            row('Battle Song', 'None', '12'),
            row('Heroic Anthem', 'Battle Song', '18'),
            row('Crescendo', 'Power Ballad', '18'),
            row('Final Encore', 'Heroic Anthem', '24')
          ].join(''));
          const augments = `
            <div class="wiki-data">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Special Effect from Augment</th>
                    <th>Gained per Augment Level</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Requiem</td>
                    <td>Masterpiece now releases homing Soul Notes from defeated enemies.</td>
                    <td>10% Soul Note Damage 2% Extra Note Chance</td>
                  </tr>
                  <tr>
                    <td>Cacophony</td>
                    <td>Discord leaves a chaotic sound trail that shreds enemy armor.</td>
                    <td>1.5s Trail Duration 3% Armor Reduction</td>
                  </tr>
                  <tr>
                    <td>Allegro</td>
                    <td>Dancing Step grants Haste and nullifies the first instance of damage.</td>
                    <td>5% Cast Speed -0.5s Internal Cooldown</td>
                  </tr>
                  <tr>
                    <td>Fortissimo</td>
                    <td>Heroic Anthem pulses healing waves to allies based on damage dealt.</td>
                    <td>1% Damage-to-Heal Conversion 5% Aura Radius</td>
                  </tr>
                </tbody>
              </table>
            </div>`;
          const section4 = `
            <div class="wiki-data">
              <table>
                <thead>
                  <tr>
                    <th>Classes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>● Viking ● Pyromancer ● Marksman ● Pirate ● Nomad ● Redneck ● Necromancer ● Samurai ● Paladin ● Amazon ● Demon Slayer ● Demonspawn ● Shaman ● White Mage ● Marauder ● Plague Doctor ● Shield Lancer ● Illusionist ● Jötunn ● Exo ● Butcher ● Stormweaver</td>
                  </tr>
                </tbody>
              </table>
            </div>`;
          mainSections = [
            { title: 'Minstrel', html: minstrel },
            { title: 'Troubadour', html: troubadour },
            { title: 'Class Augments', html: augments },
            { title: 'Classes', html: section4 }
          ];
        }
        setWikiData({
          weapon: data.weapon,
          sections: [...mainSections, ...extraSections]
        });
      } else {
        console.error("Documento não encontrado para:", tryIds.join(', '));
        if (nome === 'Bard') {
          const chest = '/images/herosiege.png';
          // URLs da Wiki
          const iconPrimaryWiki = (base) => `https://herosiege.wiki.gg/images/Monk_${base}.png`;
          const iconAltWiki = (base) => `https://herosiege.wiki.gg/images/Icon_Monk_${base}.png`;
          
          // URLs Locais
          const iconPrimaryLocal = (base) => `/images/bard/Bard_${base}.png`;

          const row = (n, r, l) => {
            const base = String(n).trim().replace(/['’!]/g, '').replace(/\s+/g, '_');
            // Tenta carregar local -> fallback para Wiki Primary -> fallback para Wiki Alt -> fallback para Chest
            const imgTag = `<img src="${iconPrimaryLocal(base)}" alt="${n}" 
                onerror="this.onerror=function(){
                    this.onerror=function(){
                        this.onerror=null;
                        this.src='${chest}'
                    };
                    this.src='${iconAltWiki(base)}'
                };
                this.src='${iconPrimaryWiki(base)}'"
            />`;

            return `
              <tr>
                <td>${imgTag}</td>
                <td>${n}</td>
                <td>${r}</td>
                <td>${l}</td>
              </tr>`;
          };
          const tableWrap = (rows) => `
            <table>
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Name</th>
                  <th>Required Skill(s)</th>
                  <th>Required Level</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>`;
          const minstrel = tableWrap([
            row('Melodic Chord', 'None', '1'),
            row('Harmony', 'None', '1'),
            row('Sound Wave', 'Melodic Chord', '6'),
            row('Discord', 'None', '6'),
            row('Vibrant Echo', 'Discord', '12'),
            row('Rhythm', 'Harmony', '12'),
            row('Symphony', 'Sound Wave', '18'),
            row('Resonance', 'Vibrant Echo', '18'),
            row('Masterpiece', 'Rhythm', '24')
          ].join(''));
          const troubadour = tableWrap([
            row('Inspiring Tune', 'None', '1'),
            row('Dancing Step', 'None', '1'),
            row('Lullaby', 'None', '6'),
            row('Power Ballad', 'Dancing Step', '6'),
            row('Chorus', 'Lullaby', '12'),
            row('Battle Song', 'None', '12'),
            row('Heroic Anthem', 'Battle Song', '18'),
            row('Crescendo', 'Power Ballad', '18'),
            row('Final Encore', 'Heroic Anthem', '24')
          ].join(''));
          const augments = `
            <div class="wiki-data">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Special Effect from Augment</th>
                    <th>Gained per Augment Level</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Requiem</td>
                    <td>Masterpiece now releases homing Soul Notes from defeated enemies.</td>
                    <td>10% Soul Note Damage 2% Extra Note Chance</td>
                  </tr>
                  <tr>
                    <td>Cacophony</td>
                    <td>Discord leaves a chaotic sound trail that shreds enemy armor.</td>
                    <td>1.5s Trail Duration 3% Armor Reduction</td>
                  </tr>
                  <tr>
                    <td>Allegro</td>
                    <td>Dancing Step grants Haste and nullifies the first instance of damage.</td>
                    <td>5% Cast Speed -0.5s Internal Cooldown</td>
                  </tr>
                  <tr>
                    <td>Fortissimo</td>
                    <td>Heroic Anthem pulses healing waves to allies based on damage dealt.</td>
                    <td>1% Damage-to-Heal Conversion 5% Aura Radius</td>
                  </tr>
                </tbody>
              </table>
            </div>`;
          const section4 = `
            <div class="wiki-data">
              <table>
                <thead>
                  <tr>
                    <th>Classes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>● Viking ● Pyromancer ● Marksman ● Pirate ● Nomad ● Redneck ● Necromancer ● Samurai ● Paladin ● Amazon ● Demon Slayer ● Demonspawn ● Shaman ● White Mage ● Marauder ● Plague Doctor ● Shield Lancer ● Illusionist ● Jötunn ● Exo ● Butcher ● Stormweaver</td>
                  </tr>
                </tbody>
              </table>
            </div>`;
          setWikiData({
            weapon: 'Instrumentos',
            sections: [
              { title: 'Minstrel', html: minstrel },
              { title: 'Troubadour', html: troubadour },
              { title: 'Class Augments', html: augments },
              { title: 'Classes', html: section4 }
            ]
          });
        }
      }
    } catch (e) {
      console.error("Erro ao buscar dados:", e);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedClass(null);
    setWikiData(null);
  };

  

  return (
    <div className="bg-[#0f111a] min-h-screen text-gray-200 font-sans selection:bg-red-500 selection:text-white">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-[#0b0d14]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              setSelectedBlogPost(null);
              navigateToView('home');
              setMobileMenuOpen(false);
            }}
          >
            <img
              src="/images/herosiege.png"
              alt="Hero Siege Brasil"
              className="block h-8 sm:h-9 w-auto"
              style={{ imageRendering: 'auto' }}
            />
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded border border-white/20 text-gray-200 hover:bg-white/10"
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              <span className="sr-only">Abrir menu</span>
              <span className="flex flex-col gap-[3px]">
                <span className="block w-5 h-[2px] bg-current" />
                <span className="block w-5 h-[2px] bg-current" />
                <span className="block w-5 h-[2px] bg-current" />
              </span>
            </button>
            <div className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-widest text-gray-400">
              <button
                onClick={() => navigateToView('home')}
                className={`transition-colors ${currentView === 'home' ? 'text-orange-500' : 'hover:text-white'}`}
              >
                Home
              </button>
              <div
                className="relative"
                ref={dbMenuRef}
                onMouseEnter={() => setIsDbOpen(true)}
                onMouseLeave={() => setIsDbOpen(false)}
              >
                <button
                    type="button"
                    onClick={() => setIsDbOpen((v) => !v)}
                    className={`transition-colors ${
                      ['classes', 'items', 'relics', 'quests', 'augments'].includes(currentView) || isDbOpen
                        ? 'text-orange-500'
                        : 'hover:text-white'
                    }`}
                  >
                    DataBase
                  </button>
                <div
                  className={`absolute left-0 top-full w-44 bg-[#0b0d14] border border-white/10 rounded shadow-xl py-2 ${
                    isDbOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                  } transition`}
                >
                  <button
                    onClick={() => {
                      navigateToView('classes');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'classes' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Classes
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('items');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'items' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Items
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('runes');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'runes' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Runas
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('relics');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'relics' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Relíquias
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('chaosTower');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'chaosTower' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Chaos Tower
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('mercenaries');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'mercenaries' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Mercenários
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('keys');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'keys' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Chaves
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('augments');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'augments' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Augments
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('quests');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'quests' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Quests
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('mining');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'mining' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Mineração
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('gems');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'gems' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Gemas e Jóias
                  </button>
                  <button
                    onClick={() => {
                      navigateToView('charms');
                      setIsDbOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'charms' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Charms
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedBlogPost(null);
                  navigateToView('blog');
                }}
                className={`transition-colors ${currentView === 'blog' ? 'text-orange-500' : 'hover:text-white'}`}
              >
                Blog
              </button>
              <div
                className="relative"
                ref={builderMenuRef}
                onMouseEnter={() => setIsBuilderOpen(true)}
                onMouseLeave={() => setIsBuilderOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setIsBuilderOpen((v) => !v)}
                  className={`transition-colors ${
                    currentView === 'builder' || isBuilderOpen
                      ? 'text-orange-500'
                      : 'hover:text-white'
                  }`}
                >
                  Builder
                </button>
                <div
                  className={`absolute left-0 top-full w-44 bg-[#0b0d14] border border-white/10 rounded shadow-xl py-2 ${
                    isBuilderOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                  } transition`}
                >
                  <button
                    onClick={() => {
                      navigateToView('builder');
                      setIsBuilderOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/5 ${
                      currentView === 'builder' ? 'text-orange-500' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Forum
                  </button>
                  <button
                    onClick={() => {
                      navigate('/hero-skills');
                      setIsBuilderOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-white hover:bg-white/5"
                  >
                    Hero Skills
                  </button>
                  <button
                    onClick={() => {
                      navigate('/hero-level-tree');
                      setIsBuilderOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-white hover:bg-white/5"
                  >
                    Hero Level Tree
                  </button>
                  <button
                    onClick={() => {
                      navigate('/ether');
                      setIsBuilderOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-white hover:bg-white/5 flex items-center justify-between"
                  >
                    <span>Ether</span>
                    <span className="text-[9px] bg-gradient-to-r from-green-400 to-emerald-600 text-black px-1.5 py-0.5 rounded-sm font-black ml-2 leading-none shadow-[0_0_8px_rgba(52,211,153,0.5)] animate-pulse">S9</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/incarnation');
                      setIsBuilderOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-white hover:bg-white/5 flex items-center justify-between"
                  >
                    <span>Incarnation</span>
                    <span className="text-[9px] bg-gradient-to-r from-green-400 to-emerald-600 text-black px-1.5 py-0.5 rounded-sm font-black ml-2 leading-none shadow-[0_0_8px_rgba(52,211,153,0.5)] animate-pulse">S9</span>
                  </button>
                </div>
              </div>
              <button
                onClick={() => navigateToView('equipe')}
                className={`transition-colors ${currentView === 'equipe' ? 'text-orange-500' : 'hover:text-white'}`}
              >
                Equipe
              </button>
              <button
                onClick={() => navigateToView('contact')}
                className={`transition-colors ${currentView === 'contact' ? 'text-orange-500' : 'hover:text-white'}`}
              >
                Contatos
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#0b0d14]">
            <div className="max-w-7xl mx-auto px-6 py-3 text-xs font-bold uppercase tracking-widest text-gray-300 space-y-1">
              <button
                className={`block w-full text-left py-1 ${currentView === 'home' ? 'text-orange-500' : ''}`}
                onClick={() => {
                  navigateToView('home');
                  setMobileMenuOpen(false);
                }}
              >
                Home
              </button>
              <button
                className={`block w-full text-left py-1 ${
                  ['classes', 'items', 'runes', 'relics', 'chaosTower', 'mercenaries', 'keys', 'augments', 'mining', 'gems', 'charms', 'quests'].includes(
                    currentView
                  )
                    ? 'text-orange-500'
                    : ''
                }`}
                onClick={() => {
                  navigateToView('classes');
                  setMobileMenuOpen(false);
                }}
              >
                Classes
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('items');
                  setMobileMenuOpen(false);
                }}
              >
                Items
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('runes');
                  setMobileMenuOpen(false);
                }}
              >
                Runas
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('relics');
                  setMobileMenuOpen(false);
                }}
              >
                Relíquias
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('chaosTower');
                  setMobileMenuOpen(false);
                }}
              >
                Chaos Tower
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('mercenaries');
                  setMobileMenuOpen(false);
                }}
              >
                Mercenários
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('keys');
                  setMobileMenuOpen(false);
                }}
              >
                Chaves
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('augments');
                  setMobileMenuOpen(false);
                }}
              >
                Augments
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('quests');
                  setMobileMenuOpen(false);
                }}
              >
                Quests
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('mining');
                  setMobileMenuOpen(false);
                }}
              >
                Mineração
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('gems');
                  setMobileMenuOpen(false);
                }}
              >
                Gemas e Jóias
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigateToView('charms');
                  setMobileMenuOpen(false);
                }}
              >
                Charms
              </button>
              <button
                className={`block w-full text-left py-1 ${currentView === 'blog' ? 'text-orange-500' : ''}`}
                onClick={() => {
                  setSelectedBlogPost(null);
                  navigateToView('blog');
                  setMobileMenuOpen(false);
                }}
              >
                Blog
              </button>
              <button
                className={`block w-full text-left py-1 ${currentView === 'builder' ? 'text-orange-500' : ''}`}
                onClick={() => {
                  navigateToView('builder');
                  setMobileMenuOpen(false);
                }}
              >
                Forum
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigate('/hero-skills');
                  setMobileMenuOpen(false);
                }}
              >
                Hero Skills
              </button>
              <button
                className="block w-full text-left py-1"
                onClick={() => {
                  navigate('/hero-level-tree');
                  setMobileMenuOpen(false);
                }}
              >
                Hero Level Tree
              </button>
              <button
                className="block w-full text-left py-1 flex items-center justify-between"
                onClick={() => {
                  navigate('/ether');
                  setMobileMenuOpen(false);
                }}
              >
                <span>Ether</span>
                <span className="text-[9px] bg-gradient-to-r from-green-400 to-emerald-600 text-black px-1.5 py-0.5 rounded-sm font-black leading-none shadow-[0_0_8px_rgba(52,211,153,0.5)]">S9</span>
              </button>
              <button
                className="block w-full text-left py-1 flex items-center justify-between"
                onClick={() => {
                  navigate('/incarnation');
                  setMobileMenuOpen(false);
                }}
              >
                <span>Incarnation</span>
                <span className="text-[9px] bg-gradient-to-r from-green-400 to-emerald-600 text-black px-1.5 py-0.5 rounded-sm font-black leading-none shadow-[0_0_8px_rgba(52,211,153,0.5)]">S9</span>
              </button>
              <button
                className={`block w-full text-left py-1 ${currentView === 'equipe' ? 'text-orange-500' : ''}`}
                onClick={() => {
                  navigateToView('equipe');
                  setMobileMenuOpen(false);
                }}
              >
                Equipe
              </button>
              <button
                className={`block w-full text-left py-1 ${currentView === 'contact' ? 'text-orange-500' : ''}`}
                onClick={() => {
                  navigateToView('contact');
                  setMobileMenuOpen(false);
                }}
              >
                Contatos
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      {currentView === 'home' && (
        <header className="relative h-[600px] overflow-hidden flex items-center">
          <div className="absolute inset-0 z-0">
            <img
              src={imageFor(heroClassName)}
              alt={heroClassName}
              className="w-full h-full object-cover opacity-20 scale-110 blur-sm"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f111a] via-[#0f111a]/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0f111a] via-transparent to-[#0f111a]" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block py-1 px-3 border border-orange-500/30 bg-orange-500/10 text-orange-500 text-xs font-bold uppercase tracking-widest mb-6 rounded-sm">
                Season 9 Updated
              </span>
              <h1 className="text-6xl md:text-8xl font-black text-white italic leading-[0.9] mb-6 tracking-tighter">
                ESCOLHA <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600 pr-2">SEU HERÓI</span>
              </h1>
              <p className="text-gray-400 text-lg max-w-md leading-relaxed mb-10 border-l-2 border-red-600 pl-6">
                Explore todas as classes, builds e segredos de Hero Siege. A base de dados definitiva para a comunidade brasileira.
              </p>
              <div className="flex gap-4">
                <button className="bg-white text-black font-black uppercase px-8 py-4 text-sm hover:bg-gray-200 transition-colors">
                  Ver Tier List
                </button>
                <button className="border border-white/20 text-white font-black uppercase px-8 py-4 text-sm hover:bg-white/5 transition-colors">
                  Patch Notes
                </button>
              </div>
            </div>

            {/* Featured Hero Image (Floating) */}
            <div className="hidden md:block relative">
              <div className="absolute inset-0 bg-red-500/20 blur-[100px] rounded-full" />
              <img
                src={imageFor(heroClassName)}
                alt={heroClassName}
                className="relative z-10 w-full max-w-lg mx-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] transform hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </header>
      )}

      {/* Main Content Grid */}
      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row gap-16">
            
            {/* Left Column: Classes Grid */}
            <div className="flex-1">
                {/* Home View: News Section */}
                {currentView === 'home' && (
                    <div className="space-y-16">
                        {/* Additional Content: Spotlight & Tier List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            
                            {/* New Class Spotlight */}
                            <div className="relative bg-[#151923] border border-white/5 overflow-hidden group h-96">
                                <div className="absolute inset-0 bg-[url('https://herosiege.wiki.gg/images/c/c5/Viking_SkinID_1.png')] bg-cover opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
                                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent z-10"></div>
                                
                                <div className="relative z-20 p-8 h-full flex flex-col justify-center items-start">
                                    <span className="inline-block py-1 px-3 bg-red-600/20 border border-red-600/50 text-red-500 text-[10px] font-bold uppercase tracking-widest mb-4 rounded-sm">
                                        New Class Spotlight
                                    </span>
                                    <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter mb-4">
                                        The <span className="text-red-600">Prophet</span>
                                    </h2>
                                    <p className="text-gray-400 text-sm max-w-xs mb-8 leading-relaxed">
                                        Dominando os elementos e invocando espíritos ancestrais, o Prophet traz uma nova mecânica de shapeshifting para o campo de batalha.
                                    </p>
                                    <button 
                                        onClick={() => {
                                            setActiveFilter('MAGIC');
                                            navigateToView('classes');
                                        }}
                                        className="bg-white text-black font-bold uppercase px-6 py-3 text-xs tracking-widest hover:bg-red-600 hover:text-white transition-all transform hover:-translate-y-1"
                                    >
                                        Ver Detalhes
                                    </button>
                                </div>

                                <img 
                                    src="https://www.mmorpgs.com.br/wp-content/uploads/2025/12/imgi_40_d4eed0645666589a854db663f8000c261191c940.webp" 
                                    alt="New Class Spotlight" 
                                    className="absolute inset-0 w-full h-full object-cover object-center z-10 drop-shadow-[0_0_50px_rgba(220,38,38,0.3)] scale-125 group-hover:scale-[1.3] transition-transform duration-700"
                                />
                            </div>

                            {/* Tier List Preview */}
                            <div className="bg-[#151923] border border-white/5 p-8 flex flex-col">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                                        Season 8 <span className="text-orange-500">Tier List</span>
                                    </h3>
                                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Updated: Today</span>
                                </div>

                                <div className="space-y-4 flex-1">
                                    {/* S Tier */}
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-red-600 flex items-center justify-center font-black text-white text-xl shadow-[0_0_15px_rgba(220,38,38,0.5)]">S</div>
                                    <div className="flex-1 flex gap-2 flex-wrap">
                                            {(tiers.S || []).map(c => (
                                                <div key={c} className="relative group/icon cursor-pointer" onClick={() => {fetchClassData(c); navigateToView('classes');}}>
                                                    <img 
                                                        src={imageFor(c)} 
                                                        alt={c} 
                                                        className="w-10 h-10 object-contain drop-shadow-md hover:scale-110 transition-transform"
                                                    />
                                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 border border-white/10 pointer-events-none">
                                                        {c}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* A Tier */}
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-orange-500 flex items-center justify-center font-black text-white text-xl shadow-[0_0_15px_rgba(249,115,22,0.5)]">A</div>
                                        <div className="flex-1 flex gap-2 flex-wrap">
                                            {(tiers.A || []).map(c => (
                                                <div key={c} className="relative group/icon cursor-pointer" onClick={() => {fetchClassData(c); navigateToView('classes');}}>
                                                    <img 
                                                        src={imageFor(c)} 
                                                        alt={c} 
                                                        className="w-10 h-10 object-contain drop-shadow-md hover:scale-110 transition-transform"
                                                    />
                                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 border border-white/10 pointer-events-none">
                                                        {c}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* B Tier */}
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-yellow-500 flex items-center justify-center font-black text-white text-xl shadow-[0_0_15px_rgba(234,179,8,0.5)]">B</div>
                                        <div className="flex-1 flex gap-2 flex-wrap">
                                            {(tiers.B || []).map(c => (
                                                <div key={c} className="relative group/icon cursor-pointer" onClick={() => {fetchClassData(c); navigateToView('classes');}}>
                                                    <img 
                                                        src={imageFor(c)} 
                                                        alt={c} 
                                                        className="w-10 h-10 object-contain drop-shadow-md hover:scale-110 transition-transform"
                                                    />
                                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 border border-white/10 pointer-events-none">
                                                        {c}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <button onClick={() => setTierModalOpen(true)} className="w-full mt-6 py-3 border border-white/10 text-gray-400 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
                                    Ver Tier List Completa
                                </button>
                            </div>
                        </div>

                        {tierModalOpen && (
                          <div className="fixed inset-0 z-50">
                            <div className="absolute inset-0 bg-black/80" onClick={() => setTierModalOpen(false)} />
                            <div className="absolute inset-0 flex items-center justify-center p-4">
                              <div className="bg-[#151923] border border-white/10 rounded-sm shadow-xl max-w-5xl w-[92vw] md:w-[80vw]">
                                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                                  <div className="flex items-center gap-3">
                                    <img src="/images/herosiege.png" alt="Hero Siege Brasil" className="h-6 w-auto"  onError={(e) => e.target.style.display = 'none'}/>
                                    <div className="text-lg font-black text-white uppercase italic tracking-widest">Tier List Completa</div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button onClick={downloadTierImage} className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-white/10 text-gray-300 hover:bg-white hover:text-black">Baixar imagem</button>
                                    <button onClick={() => setTierModalOpen(false)} className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-white/10 text-gray-300 hover:bg-white hover:text-black">Fechar</button>
                                  </div>
                                </div>
                                <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: '75vh' }}>
                                  {['S','A','B','C','D','E'].map((k) => (
                                    <div key={k} className="flex items-center gap-4 rounded-sm border-l pl-4" style={tierRowStyle(k)}>
                                      <div className="w-12 h-12 flex items-center justify-center font-black text-white text-xl" style={{ backgroundColor: k==='S'?'#dc2626':k==='A'?'#f97316':k==='B'?'#eab308':k==='C'?'#84cc16':k==='D'?'#22c55e':'#6b7280' }}>{k}</div>
                                      <div className="flex-1 flex gap-2 flex-wrap">
                                        {(tiers[k] || []).map((c) => (
                                          <div key={`${k}-${c}`} className="relative group/icon">
                                            <img src={imageFor(c)} alt={c} className="w-10 h-10 object-contain drop-shadow-md"  onError={(e) => e.target.style.display = 'none'}/>
                                            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap z-50 border border-white/10 pointer-events-none">
                                              {c}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* News Grid (from Blog posts) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {(blogPosts.slice(0,6)).map((post) => (
                            <div key={post.id} onClick={() => openBlogPost(post)} className="group relative bg-[#151923] border border-white/5 overflow-hidden cursor-pointer hover:border-orange-500/50 transition-all">
                              <div className="h-40 overflow-hidden relative flex items-center justify-center bg-black/50">
                                <div className="absolute inset-0 bg-gradient-to-t from-[#151923] to-transparent z-10"></div>
                                <img
                                  src={imageOrFallback(post.image)}
                                  alt={post.title}
                                  onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                                  className="h-full object-contain opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                                />
                              </div>
                              <div className="p-5 relative z-20 -mt-2">
                                <span className="text-[10px] text-orange-500 font-bold uppercase tracking-widest">{formatDateTime(post)}</span>
                                <h4 className="text-white font-bold text-lg mt-1 leading-tight group-hover:text-orange-500 transition-colors">
                                  {post.title}
                                </h4>
                                {post.excerpt && (
                                  <p className="text-gray-500 text-xs mt-2 leading-relaxed">
                                    {post.excerpt}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                    </div>
                )}

                {/* Classes View: Filter & Grid */}
                {currentView === 'classes' && (
                  <ClassesView
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    filteredClasses={filteredClasses}
                    onSelectClass={fetchClassData}
                    classImagePath={classImagePath}
                  />
                )}

                {/* Items View */}
                {currentView === 'items' && (
                  <ItemsView
                    itemCategories={itemCategories}
                    selectedItemCategory={selectedItemCategory}
                    itemsList={itemsList}
                    itemsLoading={itemsLoading}
                    selectedItem={selectedItem}
                    onSelectCategory={loadItemsForCategory}
                    onBackToCategories={() => {
                      setSelectedItemCategory(null);
                      setItemsList([]);
                    }}
                    onSelectItem={setSelectedItem}
                    onCloseItem={() => setSelectedItem(null)}
                  />
                )}

                {currentView === 'runes' && (
                  <RunesView />
                )}

                {currentView === 'builder' && (
                  <div className="animate-fade-in">
                    <div className="max-w-7xl mx-auto px-4 md:px-6 mb-8">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div>
                          <h2 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter">
                            Forum de <span className="text-orange-500">Builds</span>
                          </h2>
                          <p className="text-xs md:text-sm text-gray-400 mt-1">
                            Escolha uma classe para ver builds da comunidade. Em breve você poderá publicar as suas.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest border border-white/20 text-white hover:bg-white hover:text-black transition-colors"
                          onClick={() => {
                            setNbTitle('');
                            setNbAuthor('');
                            setNbContent('');
                            const baseClass = forumSelectedClass || '';
                            setNbClass(baseClass);
                            setNbType('iniciante');
                            setNbStats({
                              strength: 0,
                              dexterity: 0,
                              intelligence: 0,
                              energy: 0,
                              armor: 0,
                              vitality: 0,
                            });
                            setNbRelics([null, null, null, null, null]);
                            setNbPotions([null, null, null, null]);
                            setNbCharms([]);
                            setNewBuildOpen(true);
                          }}
                        >
                          Nova Build
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {[...CLASS_DATA].sort((a, b) => {
                          if (a.name === 'Prophet') return 1;
                          if (b.name === 'Prophet') return -1;
                          return 0;
                        }).map((c) => {
                          const selected = forumSelectedClass === c.name;
                          const slug = slugifyClass(c.name);
                          const count = buildCountsByForum[slug] || 0;
                          const countLabel = count === 1 ? '1 build' : `${count} builds`;
                          return (
                            <button
                              key={c.name}
                              type="button"
                              onClick={() =>
                                setForumSelectedClass((prev) => (prev === c.name ? null : c.name))
                              }
                              className={`relative flex flex-col items-stretch px-3 py-3 border text-left bg-gradient-to-b from-[#181b25] to-[#0c0e17] hover:from-[#1f2431] hover:to-[#10131d] transition-colors shadow-[0_0_0_1px_rgba(255,255,255,0.03),inset_0_0_0_1px_rgba(15,23,42,0.9)] ${
                                selected
                                  ? 'border-orange-500 shadow-[0_0_18px_rgba(249,115,22,0.5)]'
                                  : 'border-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center">
                                  <img
                                    src={classImagePath(c.name)}
                                    onError={(e) => {
                                      const t = e.currentTarget;
                                      if (!t.dataset.fallback) {
                                        t.dataset.fallback = '1';
                                        t.src = classImagePath(c.name, 'legacy');
                                      }
                                    }}
                                    alt={c.name}
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs font-bold text-white uppercase tracking-widest">
                                    {c.name}
                                  </div>
                                  <div className="text-[10px] text-gray-400 uppercase tracking-widest">
                                    {countLabel}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2 flex items-end justify-end text-[10px] text-gray-500">
                                {c.name === 'Prophet' && (
                                  <span className="mr-2 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-semibold animate-pulse">
                                    Nova Classe
                                  </span>
                                )}
                                {selected && (
                                  <span className="px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 font-semibold">
                                    Aberto
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-4 border border-dashed border-white/15 rounded-sm bg-[#0f111a] p-4">
                        {forumSelectedClass ? (
                          (() => {
                            const selectedSlug = slugifyClass(forumSelectedClass);
                            const list = buildsByForum[selectedSlug] || [];
                            const count = list.length;
                            return (
                              <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                  Builds para
                                </div>
                                <div className="text-sm font-bold text-white mb-1">
                                  {forumSelectedClass}
                                </div>
                                <div className="text-[11px] text-gray-400 mb-3">
                                  {count === 0
                                    ? 'Nenhuma build publicada ainda nesta área.'
                                    : count === 1
                                    ? '1 build publicada nesta área.'
                                    : `${count} builds publicadas nesta área.`}
                                </div>
                                {count > 0 && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
                                    {list.map((b) => {
                                      const tagsArr = Array.isArray(b.tags) ? b.tags : [];
                                      const normTags = tagsArr.map((t) =>
                                        String(t || '')
                                          .normalize('NFD')
                                          .replace(/[\u0300-\u036f]/g, '')
                                          .toLowerCase()
                                      );
                                      let tierType = null;
                                      if (normTags.includes('final')) tierType = 'final';
                                      else if (normTags.includes('atualizada') || normTags.includes('avancada')) tierType = 'avancada';
                                      else if (normTags.includes('iniciante')) tierType = 'iniciante';
                                      let badgeClass = 'bg-gray-500/20 text-gray-300';
                                      let badgeLabel = '';
                                      let badgeIcon = '';
                                      if (tierType === 'iniciante') {
                                        badgeClass = 'bg-emerald-500/20 text-emerald-400';
                                        badgeLabel = 'Iniciante';
                                        badgeIcon = '🌱';
                                      } else if (tierType === 'avancada') {
                                        badgeClass = 'bg-amber-500/20 text-amber-400';
                                        badgeLabel = 'Avançada';
                                        badgeIcon = '🛠️';
                                      } else if (tierType === 'final') {
                                        badgeClass = 'bg-red-500/20 text-red-400';
                                        badgeLabel = 'Final';
                                        badgeIcon = '🏁';
                                      }
                                      const clsName = b.className || b.heroClass || forumSelectedClass || '';
                                      const createdLabel = formatBuildTimestamp(b);
                                      return (
                                        <button
                                          key={b.id}
                                          type="button"
                                          onClick={() => {
                                            const slug = b.slug || b.id;
                                            navigate(`/build/${slug}`);
                                          }}
                                          className="w-full text-left border border-white/10 bg-gradient-to-b from-[#181b25] to-[#0c0e17] hover:from-[#1f2431] hover:to-[#10131d] px-3 py-2 text-[11px] flex flex-col gap-1 shadow-[0_0_0_1px_rgba(255,255,255,0.03),inset_0_0_0_1px_rgba(15,23,42,0.9)]"
                                        >
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="font-semibold text-white truncate">
                                              {clsName}
                                            </div>
                                            {badgeLabel && (
                                              <div className="flex flex-col items-end">
                                                <span className="text-[9px] uppercase tracking-widest text-gray-400 mb-0.5">
                                                  Build
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${badgeClass}`}>
                                                  <span className="mr-1">{badgeIcon}</span>
                                                  {badgeLabel}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          {clsName && (
                                            <div className="text-[10px] text-gray-400 mb-1 truncate">
                                              Área para discussão sobre builds de {clsName}
                                            </div>
                                          )}
                                          <div className="font-semibold text-gray-100 truncate">
                                            {b.title || '(sem título)'}
                                          </div>
                                          <div className="text-[10px] text-gray-400 mt-1 truncate">
                                            {(b.author || createdLabel) && (
                                              <>
                                                {b.author && <span>Autor: {b.author}</span>}
                                                {b.author && createdLabel && <span> • </span>}
                                                {createdLabel && <span>{createdLabel}</span>}
                                              </>
                                            )}
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <div className="text-xs text-gray-500">
                            Selecione uma classe acima para ver as builds da comunidade.
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                )}
                {buildModalOpen && selectedBuild && (
                  <div className="fixed inset-0 z-50">
                    <Helmet>
                      <title>{selectedBuild.title ? `${selectedBuild.title} | ` : ''}Builds | Hero Siege Brasil</title>
                      <meta name="description" content={`Confira a build ${selectedBuild.title} criada por ${selectedBuild.author}.`} />
                      <script type="application/ld+json">
                        {JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "TechArticle",
                          "headline": selectedBuild.title,
                          "author": {
                            "@type": "Person",
                            "name": selectedBuild.author
                          },
                          "datePublished": selectedBuild.createdAt?.seconds 
                            ? new Date(selectedBuild.createdAt.seconds * 1000).toISOString() 
                            : new Date().toISOString()
                        })}
                      </script>
                    </Helmet>
                    <div className="absolute inset-0 bg-black/80" onClick={() => { setBuildModalOpen(false); setSelectedBuild(null); }} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div className="bg-[#151923] border border-white/10 rounded-sm shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                          <div>
                            <div className="text-sm font-black text-white uppercase tracking-widest">
                              {typeof selectedBuild.title === 'string' ? selectedBuild.title : '(sem título)'}
                            </div>
                            <div className="text-[11px] text-gray-400 mt-1">
                              {(() => {
                                const cVal = selectedBuild.className || selectedBuild.heroClass;
                                const cName = typeof cVal === 'object' ? (cVal.name || cVal.id || '') : (cVal || '');
                                const aVal = selectedBuild.author;
                                const aName = typeof aVal === 'object' ? (aVal.name || aVal.id || '') : (aVal || '');
                                return (
                                  <>
                                    {cName}{aName ? ` · ${aName}` : ''}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-white/10 text-gray-300 hover:bg-white hover:text-black transition-colors"
                            onClick={() => { setBuildModalOpen(false); setSelectedBuild(null); }}
                          >
                            Fechar
                          </button>
                        </div>
                        <div className="p-5 overflow-y-auto text-sm leading-relaxed custom-scrollbar">
                          {(() => {
                            const tagsArr = Array.isArray(selectedBuild?.tags) ? selectedBuild.tags : [];
                            const normTags = tagsArr.map((t) =>
                              String(t || '')
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .toLowerCase()
                            );
                            let tierType = null;
                            if (normTags.includes('final')) tierType = 'final';
                            else if (normTags.includes('atualizada') || normTags.includes('avancada')) tierType = 'avancada';
                            else if (normTags.includes('iniciante')) tierType = 'iniciante';
                            let badgeClass = 'bg-gray-500/20 text-gray-300';
                            let badgeLabel = '';
                            let badgeIcon = '';
                            if (tierType === 'iniciante') {
                              badgeClass = 'bg-emerald-500/20 text-emerald-400';
                              badgeLabel = 'Iniciante';
                              badgeIcon = '🌱';
                            } else if (tierType === 'avancada') {
                              badgeClass = 'bg-amber-500/20 text-amber-400';
                              badgeLabel = 'Avançada';
                              badgeIcon = '🛠️';
                            } else if (tierType === 'final') {
                              badgeClass = 'bg-red-500/20 text-red-400';
                              badgeLabel = 'Final';
                              badgeIcon = '🏁';
                            }
                            const stats = selectedBuild?.stats || {};
                            const statItems = [
                              { key: 'strength', label: 'Strength', color: '#92400e' },
                              { key: 'dexterity', label: 'Dexterity', color: '#22c55e' },
                              { key: 'intelligence', label: 'Intelligence', color: '#ec4899' },
                              { key: 'energy', label: 'Energy', color: '#0ea5e9' },
                              { key: 'armor', label: 'Armor', color: '#4b5563' },
                              { key: 'vitality', label: 'Vitality', color: '#ef4444' },
                            ].filter(({ key }) => (Number(stats?.[key]) || 0) > 0);
                            const relicSlots = Array.isArray(selectedBuild?.relics) ? selectedBuild.relics : [];
                            const relicItems = relicSlots
                              .map((val, idx) => {
                                if (!val) return null;
                                let name = val;
                                let img = null;
                                if (typeof val === 'object') {
                                  name = val.name || 'Unknown Relic';
                                  img = val.image || val.img;
                                }

                                const base = ALL_RELICS.find((r) => r.name === name);
                                if (!base && typeof val !== 'object') return null;

                                if (base) {
                                  img = relicImageFor(base);
                                } else if (!img) {
                                  img = relicImageFor({ name });
                                }

                                return {
                                  ...base,
                                  key: `${name}-${idx}`,
                                  name,
                                  img,
                                  quest: idx === 4,
                                };
                              })
                              .filter(Boolean);
                            const potionSlots = Array.isArray(selectedBuild?.potions) ? selectedBuild.potions : [];
                            const potionItems = potionSlots
                              .map((val, idx) => {
                                if (!val) return null;
                                let name = val;
                                let img = null;
                                if (typeof val === 'object') {
                                  name = val.name || 'Unknown Potion';
                                  img = val.image || val.img;
                                }

                                const base = potionOptions.find((p) => p.name === name);
                                if (base) {
                                  img = base.image || base.img;
                                }

                                return {
                                  ...base,
                                  key: `${name}-${idx}`,
                                  name,
                                  img,
                                };
                              })
                              .filter(Boolean);
                            const mercenaryId = selectedBuild?.mercenary || null;
                            const mercenaryInfo = (() => {
                              if (!mercenaryId) return null;
                              const all = [
                                {
                                  id: 'knight',
                                  name: 'Knight',
                                  image: 'https://static.wikia.nocookie.net/herosiege/images/f/f0/Melee_Mercenary.gif'
                                },
                                {
                                  id: 'archer',
                                  name: 'Archer',
                                  image: 'https://static.wikia.nocookie.net/herosiege/images/d/d9/Ranged_Mercenary.gif'
                                },
                                {
                                  id: 'magister',
                                  name: 'Magister',
                                  image: 'https://static.wikia.nocookie.net/herosiege/images/b/be/Spell_Mercenary.gif'
                                }
                              ];
                              return all.find((m) => m.id === mercenaryId) || null;
                            })();
                            const charmSlots = Array.isArray(selectedBuild?.charms) ? selectedBuild.charms : [];
                            const charmItems = charmSlots
                              .map((charm, idx) => {
                                if (!charm) return null;
                                const pub = process.env.PUBLIC_URL || '';
                                const basePath = pub ? `${pub}/images` : `images`;
                                const img = charm.image || (charm.file ? `${basePath}/${charm.file}` : null);
                                const nameStr = (typeof charm === 'string' ? charm : charm.name) || charm.id || 'Charm';
                                return {
                                  ...charm,
                                  key: `${charm.id || idx}-${idx}`,
                                  name: nameStr,
                                  img: img,
                                  rarity: charm.rarity,
                                };
                              })
                              .filter(Boolean);

                            const weaponSlots = Array.isArray(selectedBuild?.weapons) ? selectedBuild.weapons : [];
                            const weaponItems = weaponSlots
                              .map((weapon, idx) => {
                                if (!weapon) return null;
                                const pub = process.env.PUBLIC_URL || '';
                                const basePath = pub ? `${pub}/images` : `images`;
                                const img = weapon.image || (weapon.file ? `${basePath}/${weapon.file}` : null);
                                const nameStr = (typeof weapon === 'string' ? weapon : weapon.name) || weapon.id || 'Weapon';
                                return {
                                  ...weapon,
                                  key: `${weapon.id || idx}-${idx}`,
                                  name: nameStr,
                                  img: img,
                                  isBiS: idx === 0,
                                };
                              })
                              .filter(Boolean);

                            const armorSlots = Array.isArray(selectedBuild?.bodyArmors) ? selectedBuild.bodyArmors : [];
                            const armorItems = armorSlots
                              .map((armor, idx) => {
                                if (!armor) return null;
                                const pub = process.env.PUBLIC_URL || '';
                                const basePath = pub ? `${pub}/images` : `images`;
                                const img = armor.image || (armor.file ? `${basePath}/${armor.file}` : null);
                                const nameStr = (typeof armor === 'string' ? armor : armor.name) || armor.id || 'Armor';
                                return {
                                  ...armor,
                                  key: `${armor.id || idx}-${idx}`,
                                  name: nameStr,
                                  img: img,
                                  isBiS: idx === 0,
                                };
                              })
                              .filter(Boolean);

                            const bootItems = (Array.isArray(selectedBuild?.boots) ? selectedBuild.boots : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Boot';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const gloveItems = (Array.isArray(selectedBuild?.gloves) ? selectedBuild.gloves : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Glove';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const helmetItems = (Array.isArray(selectedBuild?.helmets) ? selectedBuild.helmets : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Helmet';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const shieldItems = (Array.isArray(selectedBuild?.shields) ? selectedBuild.shields : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Shield';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const amuletItems = (Array.isArray(selectedBuild?.amulets) ? selectedBuild.amulets : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Amulet';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const beltItems = (Array.isArray(selectedBuild?.belts) ? selectedBuild.belts : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Belt';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                            }).filter(Boolean);

                            const ringItems = (Array.isArray(selectedBuild?.rings) ? selectedBuild.rings : []).map((item, idx) => {
                              if (!item) return null;
                              const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Ring';
                              let bisLabel = null;
                              if (idx === 0) bisLabel = 'BIS Esq';
                              if (idx === 1) bisLabel = 'BIS Dir';
                              return { ...item, key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 || idx === 1, bisLabel };
                            }).filter(Boolean);
                            return (
                              <>
                                {(badgeLabel || statItems.length > 0 || relicItems.length > 0 || potionItems.length > 0 || mercenaryInfo || weaponItems.length > 0 || shieldItems.length > 0 || helmetItems.length > 0 || armorItems.length > 0 || gloveItems.length > 0 || bootItems.length > 0 || beltItems.length > 0 || amuletItems.length > 0 || ringItems.length > 0 || charmItems.length > 0) && (
                                  <div className="mb-4 border border-white/10 bg-black/30 rounded-sm p-4 space-y-4">
                                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                                      <div className="flex items-center gap-2">
                                        {(() => {
                                          const cVal = selectedBuild?.className || selectedBuild?.heroClass;
                                          const cls = typeof cVal === 'object' ? (cVal.name || cVal.id || '') : (cVal || '');
                                          if (!cls) return <div className="w-10 h-10 border border-white/10 rounded flex items-center justify-center text-gray-500">?</div>;
                                          const img = classImagePath(cls, 'webp');
                                          return (
                                            <>
                                              <img
                                                src={img}
                                                alt={cls}
                                                onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                                                className="w-10 h-10 object-contain border border-white/10 rounded"
                                              />
                                              <div className="flex flex-col">
                                                <span className="text-[10px] uppercase tracking-widest text-yellow-400">
                                                  Classe
                                                </span>
                                                <span className="text-sm text-gray-200">{cls}</span>
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                      {badgeLabel && (
                                        <div className="flex flex-col items-end">
                                          <span className="text-[9px] uppercase tracking-widest text-yellow-400 mb-0.5">
                                            Tipo da Build
                                          </span>
                                          <span className={`px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest text-[10px] ${badgeClass}`}>
                                            <span className="mr-1">{badgeIcon}</span>
                                            {badgeLabel}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {statItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Atributos
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                          {statItems.map(({ key, label, color }) => (
                                            <div key={key} className="flex items-center gap-2 border px-2 py-1" style={{ borderColor: color }}>
                                              <div className="w-5 h-5">
                                                <svg viewBox="0 0 24 24" className="w-full h-full">
                                                  <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill="none" />
                                                  <path d="M12 4 L14.59 9.26 L20.24 9.91 L15.88 13.64 L17.18 19.19 L12 16.2 L6.82 19.19 L8.12 13.64 L3.76 9.91 L9.41 9.26 Z" stroke={color} strokeWidth="1.4" fill="none" strokeLinejoin="round" />
                                                </svg>
                                              </div>
                                              <span className="text-xs" style={{ color }}>{label}</span>
                                              <span className="ml-auto text-xs text-gray-300">{stats[key]}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {weaponItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Armas
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {weaponItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {shieldItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Escudos
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {shieldItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {helmetItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Capacetes
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {helmetItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {armorItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Armaduras
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {armorItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {gloveItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Luvas
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {gloveItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {bootItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Botas
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {bootItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {beltItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Cintos
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {beltItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {amuletItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Amuletos
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {amuletItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {ringItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Anéis
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {ringItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                item.isBiS ? 'border-yellow-500/60 bg-yellow-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                              {item.isBiS && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-yellow-500 text-yellow-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  {item.bisLabel || 'BIS'}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {charmItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Charms
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {charmItems.map((item) => (
                                            <div
                                              key={item.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(item);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/40"
                                            >
                                              {item.img ? (
                                                <img src={item.img} alt={item.name} className="w-5 h-5 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">?</div>
                                              )}
                                              <span className="text-[11px] text-gray-200">{item.name}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {relicItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Relíquias
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {relicItems.map((rel) => (
                                            <div
                                              key={rel.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(rel);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className={`relative flex items-center gap-2 border px-2 py-1 ${
                                                rel.quest ? 'border-red-500/60 bg-red-900/20' : 'border-white/10 bg-black/40'
                                              }`}
                                            >
                                              <img
                                                src={rel.img}
                                                alt={rel.name}
                                                className="w-5 h-5 object-contain"
                                                onError={(e) => handleRelicError(e, rel.name)}
                                              />
                                              <span className="text-[11px] text-gray-200">
                                                {rel.name}
                                              </span>
                                              {rel.quest && (
                                                <span className="absolute -top-2 -right-1 bg-black border border-red-500 text-red-500 text-[7px] px-1 rounded shadow-sm font-bold uppercase tracking-wider">
                                                  5ª Relíquia
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {potionItems.length > 0 && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Poções
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {potionItems.map((pot) => (
                                            <div
                                              key={pot.key}
                                              onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setHoverPos({ x: rect.right + 10, y: rect.top });
                                                setHoveredItem(pot);
                                              }}
                                              onMouseLeave={() => setHoveredItem(null)}
                                              className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/40"
                                            >
                                              {pot.img ? (
                                                <img
                                                  src={pot.img}
                                                  alt={pot.name}
                                                  className="w-5 h-5 object-contain"
                                                />
                                              ) : (
                                                <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">
                                                  ?
                                                </div>
                                              )}
                                              <span className="text-[11px] text-gray-200">
                                                {pot.name}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {mercenaryInfo && (
                                      <div>
                                        <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                          Mercenário
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          <div
                                            onMouseEnter={(e) => {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              setHoverPos({ x: rect.right + 10, y: rect.top });
                                              setHoveredItem(mercenaryInfo);
                                            }}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/40"
                                          >
                                            <img
                                              src={mercenaryInfo.image}
                                              alt={mercenaryInfo.name}
                                              className="w-5 h-5 object-contain"
                                            />
                                            <span className="text-[11px] text-gray-200">
                                              {mercenaryInfo.name}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {buildModalContentHtml ? (
                                  <div className="border-t border-white/10 pt-4 mt-4">
                                    <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                      Conteúdo detalhado
                                    </div>
                                    <div
                                      className="prose prose-invert prose-sm max-w-none"
                                      dangerouslySetInnerHTML={{ __html: buildModalContentHtml }}
                                    />
                                  </div>
                                ) : (
                                  <div className="border-t border-white/10 pt-4 mt-4">
                                    <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                      Conteúdo detalhado
                                    </div>
                                    <div className="text-gray-500 text-sm">
                                      Nenhum conteúdo detalhado foi cadastrado para esta build.
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {newBuildOpen && (
                  <div className="fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/80" onClick={() => setNewBuildOpen(false)} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div className="bg-[#151923] border border-white/10 rounded-sm shadow-xl max-w-6xl w-full max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                          <div className="text-sm font-black text-yellow-400 uppercase tracking-widest">
                            Nova Build
                          </div>
                          <button
                            type="button"
                            className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-white/10 text-gray-300 hover:bg-white hover:text-black transition-colors"
                            onClick={() => setNewBuildOpen(false)}
                          >
                            Fechar
                          </button>
                        </div>
                        <div className="p-5 overflow-y-auto custom-scrollbar">
                          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)] gap-4">
                            <div className="md:col-start-1 md:row-start-1">
                              <div className="border border-white/10 bg-[#0f111a] p-4 space-y-4">
                                <label className="block text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">
                                  Título da Build
                                </label>
                                <input
                                  value={nbTitle}
                                  onChange={(e) => setNbTitle(e.target.value)}
                                  className="w-full bg-[#0f111a] border border-white/10 text-sm text-white px-3 py-2"
                                  placeholder="Ex.: Bard Solo Map Farm"
                                />
                                <label className="block text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">
                                  Autor / Nickname
                                </label>
                                <input
                                  value={nbAuthor}
                                  onChange={(e) => setNbAuthor(e.target.value)}
                                  className="w-full bg-[#0f111a] border border-white/10 text-sm text-white px-3 py-2"
                                  placeholder="Seu nick"
                                />
                                <label className="block text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">
                                  Classe
                                </label>
                                <select
                                  value={nbClass}
                                  onChange={(e) => setNbClass(e.target.value)}
                                  className="w-full bg-[#0f111a] border border-white/10 text-sm text-white px-3 py-2"
                                >
                                  <option value="">Selecione</option>
                                  {CLASS_DATA.map((c) => (
                                    <option key={c.name} value={c.name}>{c.name}</option>
                                  ))}
                                </select>
                                <label className="block text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">
                                  Tipo da Build
                                </label>
                                <select
                                  value={nbType}
                                  onChange={(e) => setNbType(e.target.value)}
                                  className="w-full bg-[#0f111a] border border-white/10 text-sm text-white px-3 py-2"
                                >
                                  <option value="iniciante">Iniciante 🌱</option>
                                  <option value="avançada">Avançada 🛠️</option>
                                  <option value="final">Final 🏁</option>
                                </select>
                                <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400">
                                  Distribuição de Pontos (Total: {NB_TOTAL}) · Restantes: {Math.max(0, NB_TOTAL - totalNbStats(nbStats))}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {[
                                    { key: 'strength', label: 'Strength', color: '#92400e' },
                                    { key: 'dexterity', label: 'Dexterity', color: '#22c55e' },
                                    { key: 'intelligence', label: 'Intelligence', color: '#ec4899' },
                                    { key: 'energy', label: 'Energy', color: '#0ea5e9' },
                                    { key: 'armor', label: 'Armor', color: '#4b5563' },
                                    { key: 'vitality', label: 'Vitality', color: '#ef4444' },
                                  ].map(({ key, label, color }) => {
                                    const val = nbStats[key] || 0;
                                    const remain = NB_TOTAL - totalNbStats(nbStats);
                                    return (
                                      <div
                                        key={key}
                                        className="border p-3 bg-[#0f111a]"
                                        style={{ borderColor: color }}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <div className="w-6 h-6">
                                              <svg
                                                viewBox="0 0 24 24"
                                                className="w-full h-full"
                                              >
                                                <circle
                                                  cx="12"
                                                  cy="12"
                                                  r="9"
                                                  stroke={color}
                                                  strokeWidth="1.5"
                                                  fill="none"
                                                />
                                                <path
                                                  d="M12 4 L14.59 9.26 L20.24 9.91 L15.88 13.64 L17.18 19.19 L12 16.2 L6.82 19.19 L8.12 13.64 L3.76 9.91 L9.41 9.26 Z"
                                                  stroke={color}
                                                  strokeWidth="1.4"
                                                  fill="none"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            </div>
                                            <div
                                              className="text-xs font-bold"
                                              style={{ color }}
                                            >
                                              {label}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px]">
                                          <button
                                            type="button"
                                            className="px-2 py-1 border border-white/10 text-white hover:bg-white hover:text-black"
                                            onClick={() =>
                                              setNbStats((s) => ({
                                                ...s,
                                                [key]: Math.max(0, (s[key] || 0) - 1),
                                              }))
                                            }
                                          >
                                            −
                                          </button>
                                          <div className="flex-1 text-center text-white">{val}</div>
                                          <button
                                            type="button"
                                            className="px-2 py-1 border border-white/10 text-white hover:bg-white hover:text-black disabled:opacity-40"
                                            onClick={() =>
                                              setNbStats((s) => ({
                                                ...s,
                                                [key]: (s[key] || 0) + 1,
                                              }))
                                            }
                                            disabled={remain <= 0}
                                          >
                                            +1
                                          </button>
                                          <button
                                            type="button"
                                            className="px-2 py-1 border border-white/10 text-white hover:bg-white hover:text-black disabled:opacity-40"
                                            onClick={() =>
                                              setNbStats((s) => {
                                                const current = s[key] || 0;
                                                const toAdd = Math.min(10, NB_TOTAL - totalNbStats(s));
                                                if (toAdd <= 0) return s;
                                                return {
                                                  ...s,
                                                  [key]: current + toAdd,
                                                };
                                              })
                                            }
                                            disabled={remain <= 0}
                                          >
                                            +10
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Relíquias
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbRelics.map((name, idx) => {
                                      const isQuestSlot = idx === 4;
                                      const rel = name ? ALL_RELICS.find((r) => r.name === name) : null;
                                      const img = rel ? relicImageFor(rel) : null;
                                      const borderColor = isQuestSlot ? 'border-red-500/60' : 'border-white/20';
                                      const bgColor = isQuestSlot ? 'bg-red-900/30' : 'bg-black/30';
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className={`w-20 h-20 flex flex-col items-center justify-center border ${borderColor} ${bgColor} text-[10px] text-gray-300 hover:border-amber-400 hover:bg-black/60`}
                                          onClick={() => setNbRelicPickerIndex(idx)}
                                        >
                                          {img ? (
                                            <>
                                              <img 
                                                src={img} 
                                                alt={name} 
                                                className="w-8 h-8 object-contain mb-1" 
                                                onError={(e) => handleRelicError(e, name)}
                                              />
                                              <span className="px-1 text-[9px] leading-tight text-center line-clamp-2">
                                                {name}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-500">
                                              {isQuestSlot ? 'Relíquia de Quest' : 'Selecionar'}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbRelicPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400">
                                        <span>Selecionar relíquia para o slot {nbRelicPickerIndex + 1}</span>
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black"
                                          onClick={() => setNbRelicPickerIndex(null)}
                                        >
                                          Fechar
                                        </button>
                                      </div>
                                      <button
                                        type="button"
                                        className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-300 hover:bg-white/10 border-b border-white/5"
                                        onClick={() => {
                                          setNbRelics((prev) => {
                                            const next = prev.slice();
                                            next[nbRelicPickerIndex] = null;
                                            return next;
                                          });
                                          setNbRelicPickerIndex(null);
                                        }}
                                      >
                                        <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[13px]">
                                          ×
                                        </div>
                                        <span>Nenhuma relíquia</span>
                                      </button>
                                      {relicOptions.map((rel) => (
                                        <button
                                          key={rel.name}
                                          type="button"
                                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5"
                                          onClick={() => {
                                            setNbRelics((prev) => {
                                              const next = prev.slice();
                                              next[nbRelicPickerIndex] = rel.name;
                                              return next;
                                            });
                                            setNbRelicPickerIndex(null);
                                          }}
                                        >
                                          <img 
                                            src={rel.img} 
                                            alt={rel.name} 
                                            className="w-7 h-7 object-contain"
                                            onError={(e) => handleRelicError(e, rel.name)}
                                          />
                                          <span>{rel.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Poções
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbPotions.map((p, idx) => {
                                      const img = p ? p.image : null;
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className="w-20 h-20 flex flex-col items-center justify-center border border-white/20 bg-black/30 text-[10px] text-gray-300 hover:border-amber-400 hover:bg-black/60"
                                          onClick={() => setNbPotionPickerIndex(idx)}
                                        >
                                          {img ? (
                                            <>
                                              <img src={img} alt={p.name} className="w-8 h-8 object-contain mb-1"  onError={(e) => e.target.style.display = 'none'}/>
                                              <span className="px-1 text-[9px] leading-tight text-center line-clamp-2">
                                                {p.name}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-500">Selecionar</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbPotionPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400">
                                        <span>Selecionar poção para o slot {nbPotionPickerIndex + 1}</span>
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black"
                                          onClick={() => setNbPotionPickerIndex(null)}
                                        >
                                          Fechar
                                        </button>
                                      </div>
                                      <button
                                        type="button"
                                        className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-300 hover:bg-white/10 border-b border-white/5"
                                        onClick={() => {
                                          setNbPotions((prev) => {
                                            const next = prev.slice();
                                            next[nbPotionPickerIndex] = null;
                                            return next;
                                          });
                                          setNbPotionPickerIndex(null);
                                        }}
                                      >
                                        <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[13px]">
                                          ×
                                        </div>
                                        <span>Nenhuma poção</span>
                                      </button>
                                      {potionOptions.map((opt) => (
                                        <button
                                          key={opt.id}
                                          type="button"
                                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5"
                                          onClick={() => {
                                            setNbPotions((prev) => {
                                              const next = prev.slice();
                                              next[nbPotionPickerIndex] = opt;
                                              return next;
                                            });
                                            setNbPotionPickerIndex(null);
                                          }}
                                        >
                                          <img src={opt.image} alt={opt.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                          <span>{opt.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* CHARMS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Charms
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbCharms.map((charm, idx) => {
                                      const isSet = charm.rarity && (charm.rarity.includes('SET') || charm.rarity.includes('Set'));
                                      const isSatanic = charm.rarity && (charm.rarity === 'SATANIC' || charm.rarity === 'Satanic');
                                      const isHeroic = charm.rarity && (charm.rarity === 'HEROIC' || charm.rarity === 'Heroic');
                                      
                                      let borderColor = 'border-white/20';
                                      if (isSet) borderColor = 'border-green-600';
                                      else if (isSatanic) borderColor = 'border-red-600';
                                      else if (isHeroic) borderColor = 'border-purple-500';

                                      const pub = process.env.PUBLIC_URL || '';
                                      const basePath = pub ? `${pub}/images` : `images`;
                                      const img = charm.file ? `${basePath}/${charm.file}` : null;

                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className={`w-20 h-20 flex flex-col items-center justify-center border ${borderColor} bg-black/30 text-[10px] text-gray-300 hover:border-amber-400 hover:bg-black/60 relative`}
                                          onClick={() => setNbCharmPickerIndex(idx)}
                                        >
                                          {img ? (
                                            <>
                                              <img src={img} alt={charm.name} className="w-8 h-8 object-contain mb-1"  onError={(e) => e.target.style.display = 'none'}/>
                                              <span className="px-1 text-[9px] leading-tight text-center line-clamp-2">
                                                {charm.name}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-500">
                                              {charm.name}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                    <button
                                      type="button"
                                      className="w-20 h-20 flex flex-col items-center justify-center border border-dashed border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white transition-colors"
                                      onClick={() => setNbCharmPickerIndex(nbCharms.length)}
                                    >
                                      <span className="text-2xl mb-1">+</span>
                                      <span className="text-[10px] uppercase tracking-widest">Adicionar</span>
                                    </button>
                                  </div>

                                  {nbCharmPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400">
                                        <span>
                                          {nbCharmPickerIndex < nbCharms.length 
                                            ? 'Gerenciar Charm' 
                                            : 'Selecionar Charm'}
                                        </span>
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black"
                                          onClick={() => setNbCharmPickerIndex(null)}
                                        >
                                          Fechar
                                        </button>
                                      </div>
                                      
                                      {nbCharmPickerIndex < nbCharms.length && (
                                        <button
                                          type="button"
                                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5"
                                          onClick={() => {
                                            setNbCharms(prev => prev.filter((_, i) => i !== nbCharmPickerIndex));
                                            setNbCharmPickerIndex(null);
                                          }}
                                        >
                                          <div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">
                                            ×
                                          </div>
                                          <span>Remover este charm</span>
                                        </button>
                                      )}

                                      {CHARM_DB.map((charm, cIdx) => {
                                        const pub = process.env.PUBLIC_URL || '';
                                        const basePath = pub ? `${pub}/images` : `images`;
                                        const img = charm.file ? `${basePath}/${charm.file}` : null;
                                        
                                        return (
                                          <button
                                            key={cIdx}
                                            type="button"
                                            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5"
                                            onClick={() => {
                                              if (nbCharmPickerIndex < nbCharms.length) {
                                                setNbCharms(prev => {
                                                  const next = [...prev];
                                                  next[nbCharmPickerIndex] = charm;
                                                  return next;
                                                });
                                              } else {
                                                setNbCharms(prev => [...prev, charm]);
                                              }
                                              setNbCharmPickerIndex(null);
                                            }}
                                          >
                                            {img ? (
                                              <img src={img} alt={charm.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                            ) : (
                                              <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">
                                                ?
                                              </div>
                                            )}
                                            <div className="flex flex-col items-start">
                                                <span>{charm.name}</span>
                                                <span className={`text-[9px] ${
                                                    charm.rarity === 'SATANIC' ? 'text-red-500' :
                                                    charm.rarity === 'SET' ? 'text-green-500' :
                                                    charm.rarity === 'HEROIC' ? 'text-purple-400' : 'text-gray-400'
                                                }`}>
                                                    {charm.rarity}
                                                </span>
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                {/* WEAPONS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Weapons
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbWeapons.map((weapon, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const pub = process.env.PUBLIC_URL || '';
                                      const basePath = pub ? `${pub}/images` : `images`;
                                      // Se o weapon tem image (url completa) usa, senão tenta montar
                                      const img = weapon ? (weapon.image || (weapon.file ? `${basePath}/${weapon.file}` : null)) : null;

                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${
                                            isBiS 
                                              ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' 
                                              : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'
                                          }`}
                                          onClick={() => setNbWeaponPickerIndex(idx)}
                                        >
                                          {isBiS && (
                                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">
                                              BiS
                                            </div>
                                          )}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">
                                            {label}
                                          </div>
                                          
                                          {img ? (
                                            <>
                                              <img 
                                                src={img} 
                                                alt={weapon.name} 
                                                className="w-8 h-8 object-contain mb-1" 
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                              />
                                              <span className="text-[9px] text-center px-1 truncate w-full text-white">
                                                {weapon.name}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-2xl opacity-20">+</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {/* Weapon Picker */}
                                  {nbWeaponPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10">
                                        <span>Selecionar Weapon ({nbWeaponPickerIndex === 0 ? 'BiS' : `${nbWeaponPickerIndex + 1}ª Opção`})</span>
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black"
                                          onClick={() => setNbWeaponPickerIndex(null)}
                                        >
                                          Fechar
                                        </button>
                                      </div>
                                      
                                      <button
                                          type="button"
                                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5"
                                          onClick={() => {
                                            setNbWeapons(prev => {
                                              const next = [...prev];
                                              next[nbWeaponPickerIndex] = null;
                                              return next;
                                            });
                                            setNbWeaponPickerIndex(null);
                                          }}
                                        >
                                          <div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div>
                                          <span>Remover item</span>
                                      </button>

                                      {loadingBuildItems ? (
                                        <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div>
                                      ) : (
                                        weaponOptions.map((item, idx) => (
                                          <button
                                            key={item.id || idx}
                                            type="button"
                                            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left"
                                            onClick={() => {
                                              setNbWeapons(prev => {
                                                const next = [...prev];
                                                next[nbWeaponPickerIndex] = item;
                                                return next;
                                              });
                                              setNbWeaponPickerIndex(null);
                                            }}
                                          >
                                            {item.image ? (
                                              <img src={item.image} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                            ) : (
                                              <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>
                                            )}
                                            <div className="flex flex-col">
                                                <span>{item.name}</span>
                                                <span className="text-[9px] text-gray-500">{item.category}</span>
                                            </div>
                                          </button>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>



                                {/* BODY ARMORS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Body Armors
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbBodyArmors.map((armor, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      
                                      const img = armor ? armor.image : null;

                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${
                                            isBiS 
                                              ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' 
                                              : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'
                                          }`}
                                          onClick={() => setNbBodyArmorPickerIndex(idx)}
                                        >
                                          {isBiS && (
                                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">
                                              BiS
                                            </div>
                                          )}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">
                                            {label}
                                          </div>
                                          
                                          {img ? (
                                            <>
                                              <img 
                                                src={img} 
                                                alt={armor.name} 
                                                className="w-8 h-8 object-contain mb-1" 
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                              />
                                              <span className="text-[9px] text-center px-1 truncate w-full text-white">
                                                {armor.name}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-2xl opacity-20">+</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {/* Body Armor Picker */}
                                  {nbBodyArmorPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10">
                                        <span>Selecionar Body Armor ({nbBodyArmorPickerIndex === 0 ? 'BiS' : `${nbBodyArmorPickerIndex + 1}ª Opção`})</span>
                                        <button
                                          type="button"
                                          className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black"
                                          onClick={() => setNbBodyArmorPickerIndex(null)}
                                        >
                                          Fechar
                                        </button>
                                      </div>
                                      
                                      <button
                                          type="button"
                                          className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5"
                                          onClick={() => {
                                            setNbBodyArmors(prev => {
                                              const next = [...prev];
                                              next[nbBodyArmorPickerIndex] = null;
                                              return next;
                                            });
                                            setNbBodyArmorPickerIndex(null);
                                          }}
                                        >
                                          <div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div>
                                          <span>Remover item</span>
                                      </button>

                                      {loadingBuildItems ? (
                                        <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div>
                                      ) : (
                                        bodyArmorOptions.map((item, idx) => (
                                          <button
                                            key={item.id || idx}
                                            type="button"
                                            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left"
                                            onClick={() => {
                                              setNbBodyArmors(prev => {
                                                const next = [...prev];
                                                next[nbBodyArmorPickerIndex] = item;
                                                return next;
                                              });
                                              setNbBodyArmorPickerIndex(null);
                                            }}
                                          >
                                            {item.image ? (
                                              <img src={item.image} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/>
                                            ) : (
                                              <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>
                                            )}
                                            <div className="flex flex-col">
                                                <span>{item.name}</span>
                                                <span className="text-[9px] text-gray-500">{item.category}</span>
                                            </div>
                                          </button>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* AUGMENTS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Augments (Body Armor)</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbAugments.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-orange-500/50 bg-orange-500/5 hover:bg-orange-500/10 hover:border-orange-400 shadow-[0_0_10px_rgba(249,115,22,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbAugmentPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-orange-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {item ? (
                                            <>
                                              <i className={`fa-solid ${item.icon} text-xl mb-1`} />
                                              <span className="text-[9px] text-center px-1 truncate w-full text-white">{item.en}</span>
                                            </>
                                          ) : (
                                            <span className="text-2xl opacity-20">+</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbAugmentPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Augment ({nbAugmentPickerIndex === 0 ? 'BiS' : `${nbAugmentPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbAugmentPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbAugments(prev => { const next = [...prev]; next[nbAugmentPickerIndex] = null; return next; }); setNbAugmentPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {AUGMENTS_DATA.map((item, idx) => (
                                        <button key={idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbAugments(prev => { const next = [...prev]; next[nbAugmentPickerIndex] = item; return next; }); setNbAugmentPickerIndex(null); }}>
                                          <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]"><i className={`fa-solid ${item.icon}`} /></div>
                                          <div className="flex flex-col"><span>{item.en}</span><span className="text-[9px] text-gray-500">{item.pt}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* BOOTS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Boots</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbBoots.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbBootPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbBootPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Boot ({nbBootPickerIndex === 0 ? 'BiS' : `${nbBootPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbBootPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbBoots(prev => { const next = [...prev]; next[nbBootPickerIndex] = null; return next; }); setNbBootPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : bootOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbBoots(prev => { const next = [...prev]; next[nbBootPickerIndex] = item; return next; }); setNbBootPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* GLOVES SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Gloves</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbGloves.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbGlovePickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbGlovePickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Glove ({nbGlovePickerIndex === 0 ? 'BiS' : `${nbGlovePickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbGlovePickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbGloves(prev => { const next = [...prev]; next[nbGlovePickerIndex] = null; return next; }); setNbGlovePickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : gloveOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbGloves(prev => { const next = [...prev]; next[nbGlovePickerIndex] = item; return next; }); setNbGlovePickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* HELMETS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Helmets</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbHelmets.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbHelmetPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbHelmetPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Helmet ({nbHelmetPickerIndex === 0 ? 'BiS' : `${nbHelmetPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbHelmetPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbHelmets(prev => { const next = [...prev]; next[nbHelmetPickerIndex] = null; return next; }); setNbHelmetPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : helmetOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbHelmets(prev => { const next = [...prev]; next[nbHelmetPickerIndex] = item; return next; }); setNbHelmetPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* SHIELD SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Shields</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbShields.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbShieldPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbShieldPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Shield ({nbShieldPickerIndex === 0 ? 'BiS' : `${nbShieldPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbShieldPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbShields(prev => { const next = [...prev]; next[nbShieldPickerIndex] = null; return next; }); setNbShieldPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : shieldOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbShields(prev => { const next = [...prev]; next[nbShieldPickerIndex] = item; return next; }); setNbShieldPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* AMULETS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Amulets</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbAmulets.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbAmuletPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbAmuletPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Amulet ({nbAmuletPickerIndex === 0 ? 'BiS' : `${nbAmuletPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbAmuletPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbAmulets(prev => { const next = [...prev]; next[nbAmuletPickerIndex] = null; return next; }); setNbAmuletPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : amuletOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbAmulets(prev => { const next = [...prev]; next[nbAmuletPickerIndex] = item; return next; }); setNbAmuletPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* BELTS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Belts</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbBelts.map((item, idx) => {
                                      const isBiS = idx === 0;
                                      const label = isBiS ? 'BiS' : idx === 1 ? 'Segunda Opção' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbBeltPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbBeltPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Belt ({nbBeltPickerIndex === 0 ? 'BiS' : `${nbBeltPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbBeltPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbBelts(prev => { const next = [...prev]; next[nbBeltPickerIndex] = null; return next; }); setNbBeltPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : beltOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbBelts(prev => { const next = [...prev]; next[nbBeltPickerIndex] = item; return next; }); setNbBeltPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* RINGS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">Rings</div>
                                  <div className="flex flex-wrap gap-2">
                                    {nbRings.map((item, idx) => {
                                      const isBiS = idx === 0 || idx === 1;
                                      const label = isBiS ? 'BiS' : idx === 2 ? 'Terceira Opção' : 'Quarta Opção';
                                      const sideLabel = idx === 0 ? 'Esquerdo' : idx === 1 ? 'Direito' : null;
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <button key={idx} type="button" className={`relative w-20 h-24 flex flex-col items-center justify-center border border-dashed transition-colors ${isBiS ? 'border-green-500/50 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.1)]' : 'border-white/20 bg-white/5 text-gray-400 hover:bg-white/10 hover:border-white/40 hover:text-white'}`} onClick={() => setNbRingPickerIndex(idx)}>
                                          {isBiS && <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">BiS</div>}
                                          {sideLabel && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-600 text-black text-[9px] font-bold px-1.5 rounded uppercase tracking-widest shadow-sm">{sideLabel}</div>}
                                          <div className="text-[9px] text-gray-500 mb-1 mt-1 text-center leading-tight px-1">{label}</div>
                                          {img ? <><img src={img} alt={item.name} className="w-8 h-8 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} /><span className="text-[9px] text-center px-1 truncate w-full text-white">{item.name}</span></> : <span className="text-2xl opacity-20">+</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {nbRingPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10"><span>Selecionar Ring ({(nbRingPickerIndex === 0 || nbRingPickerIndex === 1) ? 'BiS' : `${nbRingPickerIndex + 1}ª Opção`})</span><button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbRingPickerIndex(null)}>Fechar</button></div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbRings(prev => { const next = [...prev]; next[nbRingPickerIndex] = null; return next; }); setNbRingPickerIndex(null); }}><div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span></button>
                                      {loadingBuildItems ? <div className="p-4 text-center text-xs text-gray-500">Carregando itens...</div> : ringOptions.map((item, idx) => (
                                        <button key={item.id || idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbRings(prev => { const next = [...prev]; next[nbRingPickerIndex] = item; return next; }); setNbRingPickerIndex(null); }}>
                                          {item.image || item.img ? <img src={item.image || item.img} alt={item.name} className="w-7 h-7 object-contain"  onError={(e) => e.target.style.display = 'none'}/> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}<div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* SOCKETS SECTION */}
                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">Runas, Gemas e Jóias</div>
                                  <div className="text-[10px] text-gray-500 mb-2">Runa, Joia ou Gema mais indicada para cada tipo de item</div>
                                  <div className="grid grid-cols-2 gap-2">
                                    {['Arma', 'Peitoral', 'Elmo', 'Luva', 'Bota', 'Cinto', 'Escudo', 'Amuleto', 'Anel Esquerdo', 'Anel Direito'].map((label, idx) => {
                                      const item = nbSockets[idx];
                                      const img = item ? (item.image || item.img) : null;
                                      return (
                                        <div key={idx} className="flex items-center justify-between bg-white/5 border border-white/10 p-2 rounded">
                                          <span className="text-[10px] text-gray-400 font-medium">{label}</span>
                                          <button
                                            type="button"
                                            className={`w-8 h-8 flex items-center justify-center border ${item ? 'border-amber-500/50 bg-amber-500/10' : 'border-white/20 bg-black/40'} hover:border-amber-400 transition-colors`}
                                            onClick={() => setNbSocketPickerIndex(idx)}
                                          >
                                            {img ? <img src={img} alt={item.name} className="w-6 h-6 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-white/20 text-xs">+</span>}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {nbSocketPickerIndex !== null && (
                                    <div className="mt-2 border border-white/10 bg-[#0b0d16] max-h-64 overflow-y-auto custom-scrollbar">
                                      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 text-[11px] text-gray-400 sticky top-0 bg-[#0b0d16] z-10">
                                        <span>Selecionar para {['Arma', 'Peitoral', 'Elmo', 'Luva', 'Bota', 'Cinto', 'Escudo', 'Amuleto', 'Anel Esquerdo', 'Anel Direito'][nbSocketPickerIndex]}</span>
                                        <button type="button" className="text-[10px] px-2 py-0.5 border border-white/20 rounded hover:bg-white hover:text-black" onClick={() => setNbSocketPickerIndex(null)}>Fechar</button>
                                      </div>
                                      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 border-b border-white/5" onClick={() => { setNbSockets(prev => { const next = [...prev]; next[nbSocketPickerIndex] = null; return next; }); setNbSocketPickerIndex(null); }}>
                                        <div className="w-7 h-7 flex items-center justify-center border border-red-500/20 text-[13px]">×</div><span>Remover item</span>
                                      </button>
                                      {SOCKET_ITEMS.map((item, idx) => (
                                        <button key={idx} type="button" className="w-full flex items-center gap-3 px-3 py-2 text-xs text-gray-200 hover:bg-white/10 border-b border-white/5 text-left" onClick={() => { setNbSockets(prev => { const next = [...prev]; next[nbSocketPickerIndex] = item; return next; }); setNbSocketPickerIndex(null); }}>
                                          {item.img ? <img src={item.img} alt={item.name} className="w-7 h-7 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <div className="w-7 h-7 flex items-center justify-center border border-white/20 text-[11px]">?</div>}
                                          <div className="flex flex-col"><span>{item.name}</span><span className="text-[9px] text-gray-500">{item.category}</span></div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="mt-4">
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-2">
                                    Mercenário
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {[
                                      {
                                        id: 'knight',
                                        name: 'Knight',
                                        image: 'https://static.wikia.nocookie.net/herosiege/images/f/f0/Melee_Mercenary.gif'
                                      },
                                      {
                                        id: 'archer',
                                        name: 'Archer',
                                        image: 'https://static.wikia.nocookie.net/herosiege/images/d/d9/Ranged_Mercenary.gif'
                                      },
                                      {
                                        id: 'magister',
                                        name: 'Magister',
                                        image: 'https://static.wikia.nocookie.net/herosiege/images/b/be/Spell_Mercenary.gif'
                                      }
                                    ].map((m) => {
                                      const selected = nbMercenary === m.id;
                                      return (
                                        <button
                                          key={m.id}
                                          type="button"
                                          className={`w-28 h-24 flex flex-col items-center justify-center border text-[10px] ${
                                            selected ? 'border-amber-400 bg-black/60 text-white' : 'border-white/20 bg-black/30 text-gray-300'
                                          } hover:border-amber-400`}
                                          onClick={() => setNbMercenary(selected ? '' : m.id)}
                                        >
                                          <img src={m.image} alt={m.name} className="w-10 h-10 object-contain mb-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                          <span className="px-1 text-[10px] leading-tight text-center">
                                            {m.name}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <label className="block text-[11px] font-bold uppercase tracking-widest text-yellow-400 mb-1">
                                  Conteúdo (opcional)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {[
                                    { s: '✅', bg: 'bg-emerald-600/20', fg: 'text-emerald-400' },
                                    { s: '❌', bg: 'bg-red-600/20', fg: 'text-red-400' },
                                    { s: '⭐', bg: 'bg-yellow-600/20', fg: 'text-yellow-400' },
                                    { s: '⚠️', bg: 'bg-amber-600/20', fg: 'text-amber-400' },
                                    { s: '🔥', bg: 'bg-orange-600/20', fg: 'text-orange-400' },
                                    { s: '🛡️', bg: 'bg-slate-600/20', fg: 'text-slate-300' },
                                    { s: '🗡️', bg: 'bg-rose-600/20', fg: 'text-rose-300' },
                                    { s: '🧪', bg: 'bg-fuchsia-600/20', fg: 'text-fuchsia-300' },
                                    { s: '💡', bg: 'bg-sky-600/20', fg: 'text-sky-300' },
                                    { s: '📌', bg: 'bg-violet-600/20', fg: 'text-violet-300' },
                                  ].map(({ s, bg, fg }) => (
                                    <button
                                      key={s}
                                      type="button"
                                      className={`px-2 py-1 text-[12px] rounded ${bg} ${fg} border border-white/10 hover:bg-white/10`}
                                      onClick={() => {
                                        const ta = nbContentRef.current;
                                        if (ta && typeof ta.selectionStart === 'number') {
                                          const start = ta.selectionStart;
                                          const end = ta.selectionEnd;
                                          const next = (nbContent || '');
                                          const updated = next.slice(0, start) + s + next.slice(end);
                                          setNbContent(updated);
                                          setTimeout(() => {
                                            ta.focus();
                                            const pos = start + s.length;
                                            ta.setSelectionRange(pos, pos);
                                          }, 0);
                                        } else {
                                          setNbContent((v) => (v || '') + s);
                                        }
                                      }}
                                      aria-label={`Inserir marcador ${s}`}
                                      title={`Inserir marcador ${s}`}
                                    >
                                      {s}
                                    </button>
                                  ))}
                                </div>
                                <textarea
                                  ref={nbContentRef}
                                  value={nbContent}
                                  onChange={(e) => setNbContent(e.target.value)}
                                  className="w-full bg-[#0f111a] border border-white/10 text-sm text-white px-3 py-2 min-h-[120px]"
                                  placeholder="Descreva os detalhes da build (itens, rotação, dicas)..."
                                />
                              </div>
                            </div>
                            <div className="md:col-start-2 md:row-start-1 md:self-start">
                              {(() => {
                                const t = nbType;
                                let badgeClass = 'bg-gray-500/20 text-gray-300';
                                let badgeLabel = '';
                                let badgeIcon = '';
                                if (t === 'iniciante') {
                                  badgeClass = 'bg-emerald-500/20 text-emerald-400';
                                  badgeLabel = 'Iniciante';
                                  badgeIcon = '🌱';
                                } else if (t === 'avançada') {
                                  badgeClass = 'bg-amber-500/20 text-amber-400';
                                  badgeLabel = 'Avançada';
                                  badgeIcon = '🛠️';
                                } else if (t === 'final') {
                                  badgeClass = 'bg-red-500/20 text-red-400';
                                  badgeLabel = 'Final';
                                  badgeIcon = '🏁';
                                }
                                const stats = nbStats || {};
                                const statItems = [
                                  { key: 'strength', label: 'Strength', color: '#92400e' },
                                  { key: 'dexterity', label: 'Dexterity', color: '#22c55e' },
                                  { key: 'intelligence', label: 'Intelligence', color: '#ec4899' },
                                  { key: 'energy', label: 'Energy', color: '#0ea5e9' },
                                  { key: 'armor', label: 'Armor', color: '#4b5563' },
                                  { key: 'vitality', label: 'Vitality', color: '#ef4444' },
                                ].filter(({ key }) => (Number(stats?.[key]) || 0) > 0);
                                const cVal = nbClass;
                                const cls = typeof cVal === 'object' ? (cVal.name || cVal.id || '') : (cVal || '');
                                const img = cls ? classImagePath(cls, 'webp') : '';
                                const relicSlots = Array.isArray(nbRelics) ? nbRelics : [];
                                const relicItems = relicSlots
                                  .map((val, idx) => {
                                    if (!val) return null;
                                    let name = val;
                                    let img = null;
                                    if (typeof val === 'object') {
                                      name = val.name || 'Unknown Relic';
                                      img = val.image || val.img;
                                    }
                                    const base = ALL_RELICS.find((r) => r.name === name);
                                    if (!base && typeof val !== 'object') return null;

                                    if (base) {
                                      img = relicImageFor(base);
                                    } else if (!img) {
                                      img = relicImageFor({ name });
                                    }
                                    return {
                                      key: `${name}-${idx}`,
                                      name,
                                      img,
                                      quest: idx === 4,
                                    };
                                  })
                                  .filter(Boolean);
                                const potionSlots = Array.isArray(nbPotions) ? nbPotions : [];
                                const potionItems = potionSlots
                                  .map((val, idx) => {
                                    if (!val) return null;
                                    let name = val;
                                    let img = null;
                                    if (typeof val === 'object') {
                                      name = val.name || (val.id || 'Potion');
                                      img = val.image || val.img;
                                    }
                                    return { 
                                      key: `${name}-${idx}`, 
                                      name, 
                                      img 
                                    };
                                  })
                                  .filter(Boolean);

                                const charmSlots = Array.isArray(nbCharms) ? nbCharms : [];
                                const charmItems = charmSlots
                                  .map((charm, idx) => {
                                    if (!charm) return null;
                                    const pub = process.env.PUBLIC_URL || '';
                                    const basePath = pub ? `${pub}/images` : `images`;
                                    const img = charm.image || (charm.file ? `${basePath}/${charm.file}` : null);
                                    const nameStr = (typeof charm === 'string' ? charm : charm.name) || charm.id || 'Charm';
                                    return {
                                      key: `${charm.id || idx}-${idx}`,
                                      name: nameStr,
                                      img: img,
                                      rarity: charm.rarity,
                                    };
                                  })
                                  .filter(Boolean);

                                const weaponSlots = Array.isArray(nbWeapons) ? nbWeapons : [];
                                const weaponItems = weaponSlots
                                  .map((weapon, idx) => {
                                    if (!weapon) return null;
                                    const pub = process.env.PUBLIC_URL || '';
                                    const basePath = pub ? `${pub}/images` : `images`;
                                    const img = weapon.image || (weapon.file ? `${basePath}/${weapon.file}` : null);
                                    const nameStr = (typeof weapon === 'string' ? weapon : weapon.name) || weapon.id || 'Weapon';
                                    return {
                                      key: `${weapon.id || idx}-${idx}`,
                                      name: nameStr,
                                      img: img,
                                      isBiS: idx === 0,
                                    };
                                  })
                                  .filter(Boolean);

                                const armorSlots = Array.isArray(nbBodyArmors) ? nbBodyArmors : [];
                                const armorItems = armorSlots
                                  .map((armor, idx) => {
                                    if (!armor) return null;
                                    const pub = process.env.PUBLIC_URL || '';
                                    const basePath = pub ? `${pub}/images` : `images`;
                                    const img = armor.image || (armor.file ? `${basePath}/${armor.file}` : null);
                                    const nameStr = (typeof armor === 'string' ? armor : armor.name) || armor.id || 'Armor';
                                    return {
                                      key: `${armor.id || idx}-${idx}`,
                                      name: nameStr,
                                      img: img,
                                      isBiS: idx === 0,
                                    };
                                  })
                                  .filter(Boolean);

                                const bootItems = (Array.isArray(nbBoots) ? nbBoots : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Boot';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const gloveItems = (Array.isArray(nbGloves) ? nbGloves : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Glove';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const helmetItems = (Array.isArray(nbHelmets) ? nbHelmets : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Helmet';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const shieldItems = (Array.isArray(nbShields) ? nbShields : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Shield';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const amuletItems = (Array.isArray(nbAmulets) ? nbAmulets : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Amulet';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const beltItems = (Array.isArray(nbBelts) ? nbBelts : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Belt';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 };
                                }).filter(Boolean);

                                const ringItems = (Array.isArray(nbRings) ? nbRings : []).map((item, idx) => {
                                  if (!item) return null;
                                  const nameStr = (typeof item === 'string' ? item : item.name) || item.id || 'Ring';
                                  return { key: idx, name: nameStr, img: item.image || item.img, isBiS: idx === 0 || idx === 1 };
                                }).filter(Boolean);

                                const socketSlots = Array.isArray(nbSockets) ? nbSockets : [];
                                const socketItems = socketSlots.map((item, idx) => {
                                  if (!item) return null;
                                  const labels = ['Arma', 'Peitoral', 'Elmo', 'Luva', 'Bota', 'Cinto', 'Escudo', 'Amuleto', 'Anel Esq.', 'Anel Dir.'];
                                  return {
                                    key: idx,
                                    name: item.name,
                                    img: item.img || item.image,
                                    slot: labels[idx]
                                  };
                                }).filter(Boolean);

                                const mercInfo = (() => {
                                  if (!nbMercenary) return null;
                                  const all = [
                                    {
                                      id: 'knight',
                                      name: 'Knight',
                                      image: 'https://static.wikia.nocookie.net/herosiege/images/f/f0/Melee_Mercenary.gif'
                                    },
                                    {
                                      id: 'archer',
                                      name: 'Archer',
                                      image: 'https://static.wikia.nocookie.net/herosiege/images/d/d9/Ranged_Mercenary.gif'
                                    },
                                    {
                                      id: 'magister',
                                      name: 'Magister',
                                      image: 'https://static.wikia.nocookie.net/herosiege/images/b/be/Spell_Mercenary.gif'
                                    }
                                  ];
                                  return all.find((m) => m.id === nbMercenary) || null;
                                })();
                                return (
                                  <div className="border border-white/10 bg-[#0f111a] p-4">
                                    <div className="text-sm font-black text-white uppercase tracking-widest">
                                      {(typeof nbTitle === 'string' ? nbTitle : (nbTitle?.name || '')) || '(sem título)'}
                                    </div>
                                    <div className="text-[11px] text-gray-400 mt-1">
                                      {nbAuthor ? `Autor: ${typeof nbAuthor === 'string' ? nbAuthor : (nbAuthor?.name || '')}` : ''}
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        {cls ? (
                                          <img
                                            src={img}
                                            alt={cls}
                                            onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                                            className="w-10 h-10 object-contain border border-white/10 rounded"
                                          />
                                        ) : (
                                          <div className="w-10 h-10 border border-white/10 rounded flex items-center justify-center text-gray-500">?</div>
                                        )}
                                        <div className="text-sm text-gray-200">{cls || 'Classe'}</div>
                                      </div>
                                      {badgeLabel && (
                                        <div className="flex flex-col items-end">
                                          <span className="text-[9px] uppercase tracking-widest text-gray-400 mb-0.5">
                                            Build
                                          </span>
                                          <span className={`px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest text-[10px] ${badgeClass}`}>
                                            <span className="mr-1">{badgeIcon}</span>
                                            {badgeLabel}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    {(badgeLabel || statItems.length > 0 || relicItems.length > 0 || potionItems.length > 0 || mercInfo || charmItems.length > 0 || weaponItems.length > 0 || armorItems.length > 0 || bootItems.length > 0 || gloveItems.length > 0 || helmetItems.length > 0 || shieldItems.length > 0 || amuletItems.length > 0 || beltItems.length > 0 || ringItems.length > 0 || socketItems.length > 0) && (
                                      <div className="mt-4 space-y-3">
                                        {statItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Atributos
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                              {statItems.map(({ key, label, color }) => (
                                                <div key={key} className="flex items-center gap-2 border px-2 py-1" style={{ borderColor: color }}>
                                                  <div className="w-5 h-5">
                                                    <svg viewBox="0 0 24 24" className="w-full h-full">
                                                      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill="none" />
                                                      <path d="M12 4 L14.59 9.26 L20.24 9.91 L15.88 13.64 L17.18 19.19 L12 16.2 L6.82 19.19 L8.12 13.64 L3.76 9.91 L9.41 9.26 Z" stroke={color} strokeWidth="1.4" fill="none" strokeLinejoin="round" />
                                                    </svg>
                                                  </div>
                                                  <span className="text-xs" style={{ color }}>{label}</span>
                                                  <span className="ml-auto text-xs text-gray-300">{stats[key]}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {socketItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Runas, Gemas e Jóias
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              {socketItems.map((item) => (
                                                <div key={item.key} className="flex items-center gap-2 border px-2 py-1 border-amber-500/30 bg-amber-500/5">
                                                  {item.img && <img src={item.img} alt={item.name} className="w-6 h-6 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                                                  <div className="flex flex-col">
                                                    <span className="text-[10px] text-gray-400">{item.slot}</span>
                                                    <span className="text-xs text-gray-200">{item.name}</span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {relicItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Relíquias
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {relicItems.map((rel) => (
                                                <div
                                                  key={rel.key}
                                                  className={`flex items-center gap-2 border px-2 py-1 ${
                                                    rel.quest ? 'border-red-500/60 bg-red-900/20' : 'border-white/10 bg-black/30'
                                                  }`}
                                                >
                                                  <img
                                                    src={rel.img}
                                                    alt={rel.name}
                                                    className="w-5 h-5 object-contain"
                                                    onError={(e) => handleRelicError(e, rel.name)}
                                                  />
                                                  <span className="text-[11px] text-gray-200">
                                                    {rel.name}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {potionItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Poções
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {potionItems.map((pot) => (
                                                <div
                                                  key={pot.key}
                                                  className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/30"
                                                >
                                                  {pot.img ? (
                                                    <img
                                                      src={pot.img}
                                                      alt={pot.name}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                  ) : (
                                                    <div className="w-5 h-5 flex items-center justify-center border border-white/20 text-[10px] text-gray-400">
                                                      ?
                                                    </div>
                                                  )}
                                                  <span className="text-[11px] text-gray-200">
                                                    {pot.name}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {charmItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Charms
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {charmItems.map((c) => (
                                                <div
                                                  key={c.key}
                                                  className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/30"
                                                >
                                                  {c.img ? (
                                                    <img
                                                      src={c.img}
                                                      alt={c.name}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                  ) : (
                                                    <span className="text-[10px] text-gray-500">?</span>
                                                  )}
                                                  <span className="text-[11px] text-gray-200">
                                                    {c.name}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {weaponItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Weapons
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {weaponItems.map((w) => (
                                                <div
                                                  key={w.key}
                                                  className={`flex items-center gap-2 border px-2 py-1 ${
                                                    w.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'
                                                  }`}
                                                >
                                                  {w.img ? (
                                                    <img
                                                      src={w.img}
                                                      alt={w.name}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                  ) : (
                                                    <span className="text-[10px] text-gray-500">?</span>
                                                  )}
                                                  <span className="text-[11px] text-gray-200">
                                                    {w.name}
                                                  </span>
                                                  {w.isBiS && (
                                                    <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {armorItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Body Armors
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {armorItems.map((a) => (
                                                <div
                                                  key={a.key}
                                                  className={`flex items-center gap-2 border px-2 py-1 ${
                                                    a.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'
                                                  }`}
                                                >
                                                  {a.img ? (
                                                    <img
                                                      src={a.img}
                                                      alt={a.name}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                  ) : (
                                                    <span className="text-[10px] text-gray-500">?</span>
                                                  )}
                                                  <span className="text-[11px] text-gray-200">
                                                    {a.name}
                                                  </span>
                                                  {a.isBiS && (
                                                    <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {nbAugments.some(a => a !== null) && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Augments
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {nbAugments.map((a, idx) => {
                                                if (!a) return null;
                                                const isBiS = idx === 0;
                                                return (
                                                  <div
                                                    key={idx}
                                                    className={`flex items-center gap-2 border px-2 py-1 ${
                                                      isBiS ? 'border-orange-500/60 bg-orange-900/20' : 'border-white/10 bg-black/30'
                                                    }`}
                                                  >
                                                    <i className={`fa-solid ${a.icon} text-sm text-gray-200`} />
                                                    <span className="text-[11px] text-gray-200">
                                                      ({a.en}) {a.pt}
                                                    </span>
                                                    {isBiS && (
                                                      <span className="text-[9px] text-orange-400 font-bold ml-1">BiS</span>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                        {bootItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Boots</div>
                                            <div className="flex flex-wrap gap-2">
                                              {bootItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {gloveItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Gloves</div>
                                            <div className="flex flex-wrap gap-2">
                                              {gloveItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {helmetItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Helmets</div>
                                            <div className="flex flex-wrap gap-2">
                                              {helmetItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {shieldItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Shields</div>
                                            <div className="flex flex-wrap gap-2">
                                              {shieldItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {amuletItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Amulets</div>
                                            <div className="flex flex-wrap gap-2">
                                              {amuletItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {beltItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Belts</div>
                                            <div className="flex flex-wrap gap-2">
                                              {beltItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {ringItems.length > 0 && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">Rings</div>
                                            <div className="flex flex-wrap gap-2">
                                              {ringItems.map((item) => (
                                                <div key={item.key} className={`flex items-center gap-2 border px-2 py-1 ${item.isBiS ? 'border-green-500/60 bg-green-900/20' : 'border-white/10 bg-black/30'}`}>
                                                  {item.img ? <img src={item.img} alt={item.name} className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <span className="text-[10px] text-gray-500">?</span>}
                                                  <span className="text-[11px] text-gray-200">{item.name}</span>
                                                  {item.isBiS && <span className="text-[9px] text-green-400 font-bold ml-1">BiS</span>}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {mercInfo && (
                                          <div>
                                            <div className="text-[11px] uppercase tracking-widest text-yellow-400 mb-2">
                                              Mercenário
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              <div className="flex items-center gap-2 border px-2 py-1 border-white/10 bg-black/30">
                                                <img
                                                  src={mercInfo.image}
                                                  alt={mercInfo.name}
                                                  className="w-5 h-5 object-contain"
                                                />
                                                <span className="text-[11px] text-gray-200">
                                                  {mercInfo.name}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {(nbContent || '').trim() && (
                                      <div className="mt-4 text-sm whitespace-pre-wrap text-gray-200">
                                        {nbContent}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="mt-5 flex justify-end gap-2">
                            <button
                              type="button"
                              className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest border border-white/20 text-white hover:bg-white hover:text-black transition-colors"
                              onClick={async () => {
                                const title = (nbTitle || '').trim();
                                const author = (nbAuthor || '').trim();
                                const heroClass = (nbClass || '').trim();
                                if (!title || !heroClass) return;
                                const forum = slugifyClass(heroClass);
                                const tags = [nbType];
                                const rawContent = (nbContent || '').trim();
                                let contentHtml = '';
                                if (rawContent) {
                                  const escaped = rawContent
                                    .replace(/&/g, '&amp;')
                                    .replace(/</g, '&lt;')
                                    .replace(/>/g, '&gt;');
                                  contentHtml = escaped.replace(/\n/g, '<br />');
                                }
                                try {
                                  await addDoc(collection(db, 'builds'), {
                                    title,
                                    author,
                                    className: heroClass,
                                    heroClass: heroClass,
                                    classSlug: forum,
                                    forum: forum,
                                    tags,
                                    status: 'pending',
                                    content_html: contentHtml,
                                    stats: { ...nbStats, total: NB_TOTAL },
                                    relics: Array.isArray(nbRelics) ? nbRelics.map((r) => (r || null)) : [null, null, null, null, null],
                                    potions: Array.isArray(nbPotions) ? nbPotions.map((p) => (p || null)) : [null, null, null, null],
                                    charms: Array.isArray(nbCharms) ? nbCharms.map(c => c || null) : [],
                                    weapons: Array.isArray(nbWeapons) ? nbWeapons.map(w => w || null) : [null, null, null, null],
                                    bodyArmors: Array.isArray(nbBodyArmors) ? nbBodyArmors.map(b => b || null) : [null, null, null, null],
                                    augments: Array.isArray(nbAugments) ? nbAugments.map(a => a || null) : [null, null, null, null],
                                    boots: Array.isArray(nbBoots) ? nbBoots.map(b => b || null) : [null, null, null, null],
                                    gloves: Array.isArray(nbGloves) ? nbGloves.map(g => g || null) : [null, null, null, null],
                                    helmets: Array.isArray(nbHelmets) ? nbHelmets.map(h => h || null) : [null, null, null, null],
                                    shields: Array.isArray(nbShields) ? nbShields.map(s => s || null) : [null, null, null, null],
                                    amulets: Array.isArray(nbAmulets) ? nbAmulets.map(a => a || null) : [null, null, null, null],
                                    belts: Array.isArray(nbBelts) ? nbBelts.map(b => b || null) : [null, null, null, null],
                                    rings: Array.isArray(nbRings) ? nbRings.map(r => r || null) : [null, null, null, null],
                                    mercenary: nbMercenary || null,
                                    createdAt: serverTimestamp(),
                                    updatedAt: serverTimestamp(),
                                  });
                                  setNewBuildOpen(false);
                                  setNbTitle('');
                                  setNbAuthor('');
                                  setNbContent('');
                                  setNbClass('');
                                  setNbType('iniciante');
                                  setNbStats({
                                    strength: 0,
                                    dexterity: 0,
                                    intelligence: 0,
                                    energy: 0,
                                    armor: 0,
                                    vitality: 0,
                                  });
                                  setNbRelics([null, null, null, null, null]);
                                  setNbPotions([null, null, null, null]);
                                  setNbCharms([]);
                                  setNbWeapons([null, null, null, null]);
                                  setNbBodyArmors([null, null, null, null]);
                                  setNbAugments([null, null, null, null]);
                                  setNbBoots([null, null, null, null]);
                                  setNbGloves([null, null, null, null]);
                                  setNbHelmets([null, null, null, null]);
                                  setNbShields([null, null, null, null]);
                                  setNbAmulets([null, null, null, null]);
                                  setNbBelts([null, null, null, null]);
                                  setNbRings([null, null, null, null]);
                                  setNbMercenary('');
                                } catch {
                                }
                              }}
                            >
                              Enviar para moderação
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {(currentView === 'chaosTower') && (
                  <ChaosTowerPage />
                )}

                {(currentView === 'mercenaries') && (
                  <MercenariosPage />
                )}

                {(currentView === 'mining') && (
                  <MineracaoPage />
                )}

                {(currentView === 'keys') && (
                  <ChavesPage />
                )}

                {(currentView === 'augments') && (
                  <div className="animate-fade-in">
                    <AugmentsPage />
                  </div>
                )}

                {(currentView === 'gems') && (
                  <GemasJoiasPage />
                )}

                {(currentView === 'charms') && (
                  <CharmsPage />
                )}

                {(currentView === 'quests') && (
                  <div className="flex flex-col items-center justify-center h-96 border border-white/5 bg-[#151923] p-12 text-center animate-fade-in">
                    <span className="text-6xl mb-6 grayscale opacity-50">🚧</span>
                    <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4">
                      Em <span className="text-red-600">Atualização</span>
                    </h2>
                    <p className="text-gray-400 max-w-md mx-auto">
                      Esta seção está sendo desenvolvida para a Season 9. Em breve novidades!
                    </p>
                  </div>
                )}

                {currentView === 'relics' && (
                  <div className="space-y-6 animate-fade-in">
                    <div className="flex items-end justify-between mb-6 border-b border-white/10 pb-4">
                      <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter">
                        Banco de <span className="text-red-600">Relíquias</span>
                      </h2>
                    </div>
                    <RelicsView db={db} />
                  </div>
                )}

                {currentView === 'contact' && (
                  <div className="animate-fade-in">
                    <div className="space-y-6 max-w-xl">
                      <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">
                        Fale <span className="text-red-600">Conosco</span>
                      </h2>
                      <div className="bg-[#151923] p-6 border border-white/10 space-y-4">
                        <input
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          placeholder="Seu nome"
                          className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 outline-none transition-colors"
                        />
                        <input
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder="Seu e-mail"
                          className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 outline-none transition-colors"
                        />
                        <select
                          value={contactSubject}
                          onChange={(e) => setContactSubject(e.target.value)}
                          className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 outline-none transition-colors"
                        >
                          <option value="Contato">Contato</option>
                          <option value="Reclamação">Reclamação</option>
                          <option value="Sugestão">Sugestão</option>
                          <option value="Parceria">Parceria</option>
                          <option value="Bug">Bug</option>
                          <option value="Outro">Outro</option>
                        </select>
                        <textarea
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                          placeholder="Sua mensagem"
                          rows="5"
                          className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 outline-none transition-colors"
                        />
                        <input
                          value={contactImageUrl}
                          onChange={(e) => setContactImageUrl(e.target.value)}
                          placeholder="Link de imagem (opcional)"
                          className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 outline-none transition-colors"
                        />
                        <div className="flex flex-col gap-2 text-xs text-gray-300">
                          <span>Verificação simples:</span>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-2 bg-black/40 border border-white/10 rounded">
                              {contactCaptchaA} {contactCaptchaOp} {contactCaptchaB} =
                            </span>
                            <input
                              value={contactCaptchaAnswer}
                              onChange={(e) => setContactCaptchaAnswer(e.target.value)}
                              placeholder="Resultado"
                              className="w-24 bg-black/30 border border-white/10 p-2 text-xs text-white focus:border-red-500 outline-none transition-colors"
                            />
                            <button
                              type="button"
                              onClick={resetContactCaptcha}
                              className="text-[10px] uppercase tracking-widest border border-white/10 px-2 py-1 hover:bg-white/5 text-gray-300"
                            >
                              Trocar
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={submitContact}
                            disabled={contactSending}
                            className="border border-white/10 text-white font-bold uppercase px-6 py-3 text-xs tracking-widest hover:bg-white/5 transition-colors disabled:opacity-50"
                          >
                            Enviar
                          </button>
                          {contactSent && <span className="text-xs text-green-400">Recebido! Obrigado pelo contato.</span>}
                          {!!contactError && <span className="text-xs text-red-400">{contactError}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Blog View */}
                {currentView === 'blog' && (
                  <div className="space-y-8">
                    {!selectedBlogPost && (
                      <>
                        <div className="flex items-end justify-between mb-6 border-b border-white/10 pb-6">
                          <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter">Blog</h2>
                          <span className="text-xs text-gray-500 font-bold uppercase tracking-widest">{blogPosts.length} posts</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {blogPosts.map(post => (
                            <div key={post.id} onClick={() => openBlogPost(post)} className="group relative bg-[#151923] border border-white/5 overflow-hidden cursor-pointer hover:border-orange-500/50 transition-all">
                              <div className="h-40 overflow-hidden relative flex items-center justify-center bg-black/50">
                                <div className="absolute inset-0 bg-gradient-to-t from-[#151923] to-transparent z-10"></div>
                                <img
                                  src={imageOrFallback(post.image)}
                                  alt={post.title}
                                  onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                                  className="h-full object-contain opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                                />
                              </div>
                              <div className="p-5 relative z-20 -mt-2">
                                <span className="text-[10px] text-orange-500 font-bold uppercase tracking-widest">{formatDateTime(post)}</span>
                                <h4 className="text-white font-bold text-lg mt-1 leading-tight group-hover:text-orange-500 transition-colors">{post.title}</h4>
                                {post.author && (
                                  <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">{post.author}</div>
                                )}
                                
                                {post.excerpt && <p className="text-gray-500 text-xs mt-2 leading-relaxed">{post.excerpt}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {selectedBlogPost && (
                      <div>
                        <Helmet>
                          <title>{selectedBlogPost.title} | Blog | Hero Siege Brasil</title>
                          <meta name="description" content={selectedBlogPost.excerpt || `Leia sobre ${selectedBlogPost.title} em Hero Siege Brasil.`} />
                          <meta property="og:title" content={`${selectedBlogPost.title} | Hero Siege Brasil`} />
                          <meta property="og:description" content={selectedBlogPost.excerpt || `Leia sobre ${selectedBlogPost.title} em Hero Siege Brasil.`} />
                          {selectedBlogPost.image && <meta property="og:image" content={selectedBlogPost.image} />}
                          <meta property="og:type" content="article" />
                          <script type="application/ld+json">
                            {JSON.stringify({
                              "@context": "https://schema.org",
                              "@type": "BlogPosting",
                              "headline": selectedBlogPost.title,
                              "image": selectedBlogPost.image ? [selectedBlogPost.image] : [],
                              "datePublished": selectedBlogPost.date?.seconds 
                                ? new Date(selectedBlogPost.date.seconds * 1000).toISOString() 
                                : new Date().toISOString(),
                              "author": [{
                                  "@type": "Person",
                                  "name": selectedBlogPost.author || "Hero Siege Brasil"
                              }],
                              "description": selectedBlogPost.excerpt || `Leia sobre ${selectedBlogPost.title}`
                            })}
                          </script>
                        </Helmet>
                        <button onClick={() => { setSelectedBlogPost(null); navigate('/blog'); }} className="text-xs uppercase tracking-widest font-bold text-gray-400 hover:text-white mb-4">← Voltar</button>
                        <div className="bg-[#151923] border border-white/5">
                          <div className="h-60 w-full overflow-hidden relative flex items-center justify-center bg-black/50">
                            <img
                              src={imageOrFallback(selectedBlogPost.image)}
                              alt={selectedBlogPost.title}
                              onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                              className="h-full object-contain"
                            />
                          </div>
                          <div className="p-6">
                            <span className="text-[10px] text-orange-500 font-bold uppercase tracking-widest">{formatDateTime(selectedBlogPost)}</span>
                            <h1 className="text-3xl font-black text-white uppercase italic tracking-tighter mt-1">{selectedBlogPost.title}</h1>
                            {selectedBlogPost.author && <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">{selectedBlogPost.author}</div>}
                            {Array.isArray(selectedBlogPost.tags) && selectedBlogPost.tags.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {selectedBlogPost.tags.map(t => (
                                  <span key={t} className="text-[10px] px-2 py-0.5 border border-white/10 text-gray-400 uppercase tracking-widest">{t}</span>
                                ))}
                              </div>
                            )}
                            {selectedBlogPost.excerpt && <p className="text-gray-400 text-sm mt-3">{selectedBlogPost.excerpt}</p>}
                            <div className="mt-6 prose prose-invert max-w-none" ref={contentRef} />
                            <div className="mt-6">
                              <button
                                onClick={() => {
                                  const url = `${window.location.origin}/blog/${selectedBlogPost.slug || selectedBlogPost.id}`;
                                  if (navigator.share) {
                                    navigator.share({ title: selectedBlogPost.title, url }).catch(() => {});
                                  } else {
                                    navigator.clipboard?.writeText(url);
                                    alert('Link copiado para a área de transferência.');
                                  }
                                }}
                                className="border border-white/10 text-white font-bold uppercase px-6 py-3 text-xs tracking-widest hover:bg-white/5 transition-colors"
                              >
                                Compartilhar
                              </button>
                            </div>
                            <div className="mt-10">
                              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-3">Comentários</h3>
                              <BlogComments postId={selectedBlogPost.id} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentView === 'equipe' && (
                  <Equipe />
                )}
            </div>

            {/* Right Column: News/Sidebar */}
            <aside className="w-full md:w-80 flex-shrink-0 space-y-12">
                {/* Search Box */}
                <div className="bg-[#151923] p-6 border border-white/5">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Database Search</h3>
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Search hero, skill, item..." 
                            className="w-full bg-black/30 border border-white/10 p-3 text-sm text-white focus:border-red-500 focus:outline-none transition-colors"
                        />
                        <span className="absolute right-3 top-3 text-gray-600">🔍</span>
                    </div>
                </div>

                <div
                  className={`bg-[#151923] border ${
                    twitchOnline === null
                      ? 'border-white/5'
                      : twitchOnline
                      ? 'border-[#00ff41]/40'
                      : 'border-[#ff3e3e]/40'
                  } rounded-sm overflow-hidden`}
                >
                  <div className="flex items-center justify-between px-4 py-2 bg-black/40 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block rounded-full w-2.5 h-2.5 ${
                          twitchOnline === null
                            ? 'bg-gray-500'
                            : twitchOnline
                            ? 'bg-[#00ff41] shadow-[0_0_8px_#00ff41]'
                            : 'bg-[#ff3e3e] shadow-[0_0_8px_#ff3e3e]'
                        }`}
                      />
                      <span
                        className={`text-[11px] font-bold uppercase tracking-widest ${
                          twitchOnline === null
                            ? 'text-gray-300'
                            : twitchOnline
                            ? 'text-[#00ff41]'
                            : 'text-[#ff3e3e]'
                        }`}
                      >
                        {twitchOnline === null ? 'Verificando...' : twitchOnline ? 'Ao vivo' : 'Offline'}
                      </span>
                    </div>
                    <a
                      href="https://twitch.tv/spacezonetv"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-[11px] text-gray-300 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0H6zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714v9.429zM11.571 4.714h1.715v5.143H11.57V4.714zm4.715 0H18v5.143h-1.714V4.714z" />
                      </svg>
                      <span>twitch.tv/spacezonetv</span>
                    </a>
                  </div>
                  <div className="relative" style={{ paddingBottom: '56.25%', height: 0, background: '#000' }}>
                    <div
                      id={TWITCH_CONTAINER_ID}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  </div>
                </div>

                <div className="bg-[#151923] p-6 border border-white/5 text-center relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-700/10 to-transparent opacity-40 group-hover:opacity-70 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <img src="https://store.steampowered.com/favicon.ico" alt="Steam" className="w-4 h-4 opacity-80"  onError={(e) => e.target.style.display = 'none'}/>
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Jogadores Online</h3>
                      {!!steamPlayers && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>}
                    </div>
                    <div className="flex items-baseline justify-center gap-3">
                      <span className="text-gray-400">
                        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="8" cy="7" r="3"></circle>
                          <circle cx="16" cy="9" r="2.5"></circle>
                          <path d="M2 20c0-3.5 3-6 6-6s6 2.5 6 6H2z"></path>
                          <path d="M13 20c0-2.5 2-4.5 4.5-4.5S22 17.5 22 20h-9z"></path>
                        </svg>
                      </span>
                      <div className="text-5xl font-black text-white tracking-tight">{steamPlayers ?? '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Season Countdown */}
                <div className="bg-[#151923] p-6 border border-white/5 relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10 text-center">
                        <span className="text-[10px] text-red-500 font-bold uppercase tracking-[0.2em] mb-2 block">Next Season</span>
                        <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-1">
                            Season <span className="text-red-600">9</span> Start
                        </h3>
                        <h4 className="text-lg font-bold text-orange-500 uppercase tracking-widest mb-4">
                            Incarnation
                        </h4>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-black/40 p-2 border border-white/5 rounded-sm">
                                <span className="block text-xl font-bold text-white">{timeLeft.days}</span>
                                <span className="text-[8px] text-gray-500 uppercase font-bold tracking-wider">Days</span>
                            </div>
                            <div className="bg-black/40 p-2 border border-white/5 rounded-sm">
                                <span className="block text-xl font-bold text-white">{timeLeft.hours}</span>
                                <span className="text-[8px] text-gray-500 uppercase font-bold tracking-wider">Hours</span>
                            </div>
                            <div className="bg-black/40 p-2 border border-white/5 rounded-sm">
                                <span className="block text-xl font-bold text-white">{timeLeft.minutes}</span>
                                <span className="text-[8px] text-gray-500 uppercase font-bold tracking-wider">Mins</span>
                            </div>
                        </div>
                        <p className="text-[10px] text-gray-600 mt-4 font-mono">Target: April 3, 2026 (Est.)</p>
                    </div>
                </div>

                {/* Latest Updates */}
                <div>
                    <h3 className="text-xl font-black text-white uppercase italic mb-6 border-l-4 border-red-600 pl-4">
                        Últimas <span className="text-red-600">Atualizações</span>
                    </h3>
                    <div className="space-y-4">
                        {(blogPosts.slice(0,3)).map((p) => (
                          <div key={p.id} className="flex gap-4 group cursor-pointer" onClick={() => openBlogPost(p)}>
                            <div className="w-20 h-20 bg-[#151923] flex-shrink-0 overflow-hidden relative">
                              {p.image ? (
                                <img
                                  src={imageOrFallback(p.image)}
                                  alt={p.title}
                                  onError={(e) => { e.currentTarget.src = '/images/herosiege.png'; }}
                                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-colors"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-800 group-hover:bg-red-900/20 transition-colors"></div>
                              )}
                            </div>
                            <div>
                              <span className="text-[10px] text-red-500 font-bold uppercase">{formatDateTime(p)}</span>
                              <h4 className="text-white font-bold leading-tight group-hover:text-red-500 transition-colors">{p.title}</h4>
                            </div>
                          </div>
                        ))}
                    </div>
                </div>

                {/* Newsletter */}
                <div className="bg-gradient-to-br from-red-600 to-red-900 p-8 text-center">
                    <h3 className="text-2xl font-black text-white italic uppercase mb-2">Join the Fight</h3>
                    <p className="text-red-100 text-xs mb-6">Comece sua jornada em Tarethiel hoje mesmo!</p>
                    <a href="https://store.steampowered.com/app/269210/Hero_Siege/" target="_blank" rel="noopener noreferrer" className="inline-block w-full bg-white text-red-900 font-bold uppercase py-3 text-xs tracking-widest hover:bg-gray-100 transition-colors">
                        Buy on Steam
                    </a>
                </div>
            </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0b0d14] border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center opacity-50 hover:opacity-100 transition-opacity">
            <div className="text-sm text-gray-500">
                Hero Siege Brasil 2026© Este site não é afiliado à Panic Art Studios. Todos os assets e dados pertencem aos seus respectivos donos.
            </div>
            <div className="flex gap-6 mt-4 md:mt-0">
                <a href="https://discord.gg/herosiegeofficial" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white cursor-pointer transition-colors">Discord HS</a>
                <a href="https://store.steampowered.com/app/269210/Hero_Siege/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white cursor-pointer transition-colors">Steam</a>
                <a href="https://www.panicartstudios.com/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white cursor-pointer transition-colors">PAS</a>
            </div>
        </div>
      </footer>

      {/* Class Detail Modal (Overlay) */}
      {selectedClass && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={closeModal}></div>
            
            <div className="relative bg-[#0f111a] w-full max-w-6xl h-[85vh] border border-white/10 flex flex-col md:flex-row shadow-2xl overflow-hidden animate-fade-in">
                
                {/* Close Button */}
                <button 
                    onClick={closeModal}
                    className="absolute top-4 right-4 z-50 w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                >
                    ✕
                </button>

                {/* Sidebar Info */}
                <div className="w-full md:w-80 bg-[#0b0d14] border-r border-white/5 relative overflow-hidden flex flex-col">
                    <div className="absolute inset-0 bg-[url('https://herosiege.wiki.gg/images/c/c5/Viking_SkinID_1.png')] bg-cover opacity-5"></div>
                    
                    <div className="w-full h-full overflow-y-auto custom-scrollbar p-8 flex flex-col items-center text-center relative z-10">
                            <img 
                            src={classImagePath(selectedClass)} 
                            onError={(e) => { const t = e.currentTarget; if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = classImagePath(selectedClass, 'legacy'); } }}
                            alt={selectedClass} 
                            className="w-48 h-48 object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] mb-6"
                        />
                        
                        <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-2">
                            {selectedClass}
                        </h2>
                        
                        <div className="w-full h-px bg-white/10 my-6 flex-shrink-0"></div>

                        {loading ? (
                            <div className="text-red-500 animate-pulse">Carregando dados...</div>
                        ) : wikiData ? (
                            <div className="w-full text-left space-y-6">
                                 <div className="bg-[#151923] p-4 border border-white/5">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-xl">⚔️</span>
                                        <span className="text-gray-400 text-xs font-bold uppercase">Equipamento</span>
                                    </div>
                                    <div className="text-white font-bold text-sm leading-tight">
                                        {wikiData.weapon}
                                    </div>
                                 </div>

                                {/* Navigation Tabs */}
                                 <div className="space-y-1">
                                    {wikiData.sections.map((section, index) => {
                                        const hasTitle = section?.title && String(section.title).trim();
                                        const label = hasTitle
                                          ? section.title
                                          : (section?.isExtra ? '🔍 Extra' : specTitleFor(selectedClass, index));
                                        return (
                                        <button
                                            key={index}
                                            onClick={() => setActiveTab(index)}
                                            className={`w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all border-l-2 select-none ${
                                                activeTab === index 
                                                ? 'bg-red-600/10 border-red-600 text-white' 
                                                : 'border-transparent text-gray-500 hover:text-white hover:bg-white/5'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                        );
                                    })}
                                 </div>
                            </div>
                        ) : (
                            <div className="text-gray-500">Selecionando...</div>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 bg-[#0f111a] relative overflow-hidden flex flex-col">
                    {/* Content Header */}
                    <div className="h-20 border-b border-white/5 flex items-center px-8 bg-[#0f111a]">
                        <h3 className="text-xl font-bold text-gray-300 uppercase">
                            {(() => {
                              const sec = wikiData?.sections?.[activeTab];
                              if (!sec) return "Detalhes";
                              const hasTitle = sec?.title && String(sec.title).trim();
                              if (hasTitle) return sec.title;
                              return sec?.isExtra ? '🔍 Extra' : specTitleFor(selectedClass, activeTab);
                            })()}
                        </h3>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                        {loading ? (
                             <div className="flex items-center justify-center h-full">
                                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                             </div>
                        ) : wikiData ? (
                            <div 
                                className="wiki-content prose prose-invert prose-red max-w-none"
                                dangerouslySetInnerHTML={{ __html: wikiData.sections[activeTab]?.html }} 
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                Selecione uma classe para ver os detalhes
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
      )}
      
      {/* Hover Modal for Build Items */}
      {hoveredItem && (
        <div 
            className="fixed z-[9999] pointer-events-none bg-[#0f111a]/95 backdrop-blur-md border border-white/20 p-4 rounded-sm shadow-2xl max-w-[300px] w-max animate-fade-in"
            style={{ 
                top: Math.min(window.innerHeight - 200, Math.max(10, hoverPos.y)), 
                left: Math.min(window.innerWidth - 320, hoverPos.x),
            }}
        >
            {/* Header */}
            <div className="flex items-start gap-3 mb-3 border-b border-white/10 pb-2">
                {hoveredItem.img && (
                    <img 
                        src={hoveredItem.img} 
                        alt={hoveredItem.name} 
                        className="w-10 h-10 object-contain bg-black/50 rounded border border-white/10" 
                        onError={(e) => e.target.style.display = 'none'}
                    />
                )}
                <div>
                    <div 
                        className={`text-sm font-bold leading-tight ${rarityStyle(hoveredItem.rarity || '').text}`}
                        style={{ textShadow: rarityStyle(hoveredItem.rarity || '').glow }}
                    >
                        {hoveredItem.name}
                    </div>
                    {(hoveredItem.rarity || hoveredItem.type) && (
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">
                            {hoveredItem.rarity} {hoveredItem.type}
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Section */}
            <div className="space-y-2 text-xs">
                {/* Relic Specific Stats */}
                {hoveredItem.stats && Array.isArray(hoveredItem.stats) && hoveredItem.l1 && (
                    <div className="space-y-1">
                        {hoveredItem.stats.map((stat, i) => (
                            <div key={i} className="flex justify-between text-gray-300 gap-4">
                                <span>{stat}:</span>
                                <span className="text-yellow-400 font-mono">
                                    {hoveredItem.l10?.[i] || hoveredItem.l1?.[i] || '?'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Generic Description/Stats */}
                 {!hoveredItem.l1 && hoveredItem.stats && !Array.isArray(hoveredItem.stats) && (
                     <div className="text-gray-300">{String(hoveredItem.stats)}</div>
                 )}

                 {/* Runewords */}
                 {hoveredItem.runeword && (
                     <div className="text-orange-400 italic">
                         Runeword: {hoveredItem.runeword}
                     </div>
                 )}

                 {/* Description */}
                 {hoveredItem.description && (
                     <div className="text-gray-400 italic text-[11px] mt-2 border-t border-white/5 pt-2">
                         {hoveredItem.description}
                     </div>
                 )}
            </div>
        </div>
      )}
      
      {/* Global Styles for this component only (Scopedish) */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: #0b0d14;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #333;
            border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #ef4444;
        }
        
        /* Wiki Content Styles Override */
        .wiki-content table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0 4px;
            margin-bottom: 2rem;
        }
        .wiki-content tr {
            background: #151923;
        }
        .wiki-content td {
            padding: 1rem;
            border: 1px solid #2a303c;
            color: #9ca3af;
        }
        .wiki-content td:first-child {
            border-left: 2px solid #ef4444;
            width: 80px;
            text-align: center;
        }
        .wiki-content img {
            max-width: 60px;
            border-radius: 4px;
            border: 1px solid #444;
        }
        .wiki-content b {
            color: #ef4444;
            display: block;
            margin-bottom: 4px;
            font-family: 'Orbitron', sans-serif;
            font-size: 0.9em;
        }
      `}</style>
    </div>
  );
};

export default NewDesign;
